// src/sound_lib/core/_SoundLibLoader.ts
import { SoundLoader } from "audio_utils";
import { BrowserUtils, ConversionUtils as ConversionUtils2, HttpUtils, IdxDbManager, JsonUtils } from "ts_utils";

// src/sound_lib/sf2/_Sf2Instrument.ts
var _Sf2Instrument = class {
  constructor() {
    this.name = "";
    this.zones = [];
  }
};

// src/sound_lib/core/presets.ts
var _BasicPreset = class {
};

// src/sound_lib/sf2/_Sf2Preset.ts
var _Sf2Preset = class extends _BasicPreset {
  constructor() {
    super(...arguments);
    this.zones = [];
  }
};

// src/sound_lib/sf2/_Sf2Sample.ts
var _Sf2Sample = class {
};

// src/sound_lib/sf2/_Sf2Zone.ts
var _Sf2Zone = class __Sf2Zone {
  constructor() {
    this.startLoopAddrsOffset = 0;
    this.endLoopAddrsOffset = 0;
    this.bottomNote = 0;
    this.topNote = 127;
    this.bottomVel = 0;
    this.topVel = 127;
    //the fine tuning in semitones (0.05 would be 5 cents sharp)
    this.fineTune = 0;
    //the course tuning in semitones (5 would be 5 semitones sharp)
    this.courseTune = 0;
    //overrides the sample's root key - if -1, it does nothing
    this.overridingRootKey = -1;
    this.loop = false;
    /**volume reduction - in centibels (10 cB per 1 dB). Positive values mean
     * reduction.*/
    this.initialAttenuation = 0;
    /**the frequency of the low pass filter. Above 20,000 or null means
     * don't use.*/
    this.initialFilterFc = 13500;
    //Information for envelope volume. The delay, attack, hold and decay all
    //refer to the length of time, in samples, that each phase should last.
    //During the attack env, volume ramps up from nothing to full (1.0). During
    //the hold, it stays steady, and then it declines during the decay env BY
    //THE AMOUNT specified in the sustainVolReduction (not TO, but BY) the
    //release env is the amount of time taken to get to 0 after the note is
    //released.
    this.delayVolEnvTime = 1e-3;
    this.attackVolEnvTime = 1e-3;
    this.holdVolEnvTime = 1e-3;
    this.decayVolEnvTime = 1e-3;
    this.sustainVolReduction = 0;
    //0 to 1
    this.releaseVolEnvTime = 1e-3;
    /**added to initialFilterFc according to mod envelope*/
    this.modEnvToFilterFc = 0;
    this.isGlobal = false;
  }
  static createForInstrument() {
    let zone = new __Sf2Zone();
    zone._zoneType = 0 /* instrument */;
    return zone;
  }
  static createForPreset() {
    let zone = new __Sf2Zone();
    zone._zoneType = 1 /* preset */;
    zone.delayVolEnvTime = 1;
    zone.attackVolEnvTime = 1;
    zone.holdVolEnvTime = 1;
    zone.decayVolEnvTime = 1;
    zone.sustainVolReduction = 0;
    zone.releaseVolEnvTime = 1;
    zone.initialFilterFc = 1;
    return zone;
  }
  /**
   * creates a duplicate of the zone with all of the same property values
   */
  clone() {
    let zone = this._zoneType === 1 /* preset */ ? __Sf2Zone.createForPreset() : __Sf2Zone.createForInstrument();
    zone.instrument = this.instrument;
    zone.startLoopAddrsOffset = this.startLoopAddrsOffset;
    zone.endLoopAddrsOffset = this.endLoopAddrsOffset;
    zone.bottomNote = this.bottomNote;
    zone.topNote = this.topNote;
    zone.sample = this.sample;
    zone.fineTune = this.fineTune;
    zone.courseTune = this.courseTune;
    zone.overridingRootKey = this.overridingRootKey;
    zone.loop = this.loop;
    zone.initialAttenuation = this.initialAttenuation;
    zone.initialFilterFc = this.initialFilterFc;
    zone.delayVolEnvTime = this.delayVolEnvTime;
    zone.attackVolEnvTime = this.attackVolEnvTime;
    zone.holdVolEnvTime = this.holdVolEnvTime;
    zone.decayVolEnvTime = this.decayVolEnvTime;
    zone.sustainVolReduction = this.sustainVolReduction;
    zone.releaseVolEnvTime = this.releaseVolEnvTime;
    zone.modEnvToFilterFc = this.modEnvToFilterFc;
    return zone;
  }
};

// src/sound_lib/sf2/_Sf2Parser.ts
var _Sf2Parser = class {
  constructor(context) {
    this._context = context;
  }
  /**parses the SF data.
   * @param sfdata the ArrayBuffer containing all of the SF data
   * @param audioIsCompressed - only set this for SF3 files that embed
   * compressed audio.
   * @param samplesPath - leave null unless you are loading audio separately.
   * This is the root path for all of the sample audio files, ending in
   * a forward slash.
   * @param samplesExtension - leave null unless you are loading audio
   * separately. This is the 4 character file extension for the audio,
   * example: ".ogg"
   * @param samplesRate - leave null unless you are loading audio separately.
   * This is the sample rate of the external audio files (all should be the
   * same).*/
  async parse(sfdata, audioIsCompressed) {
    this._sfData = sfdata;
    this._audioIsCompressed = audioIsCompressed;
    this._createReaders();
    await this._createSamples();
    this._createInstruments();
    this._createPresets();
  }
  /**
   * gets the Preset object with the requested bank and preset numbers
   * @param preset the preset number from the SF2 file
   * @param bank the bank number from the SF2 file
   * @return the Preset object if it is found, otherwise null
   */
  getPreset(bank, preset) {
    if (this._presets == null)
      return null;
    let numPresets = this._presets.length;
    for (let i = 0; i < numPresets; i++) {
      if (this._presets[i].presetNumber === preset) {
        if (this._presets[i].bankNumber === bank) {
          return this._presets[i];
        }
      }
    }
    return null;
  }
  _createReaders() {
    let _log = (result) => {
    };
    let dataRdr = new _BinaryListReader(this._sfData);
    _log(dataRdr.readString(4));
    _log(dataRdr.readUint(4));
    _log(dataRdr.readString(4));
    _log(dataRdr.readString(4));
    dataRdr.getRangeAsNewReader(dataRdr.readUint(4));
    _log(dataRdr.readString(4));
    let sdtaLength = dataRdr.readUint(4);
    let startOfSdtaSection = dataRdr.position;
    _log("sdtaLength: $sdtaLength");
    _log(dataRdr.readString(4));
    this._samplesSectionByteIndex = dataRdr.position;
    _log(dataRdr.readString(4));
    dataRdr.position = startOfSdtaSection + sdtaLength;
    _log(dataRdr.readString(4));
    dataRdr.readUint(4);
    _log(dataRdr.readString(4));
    _log(dataRdr.readString(4));
    this._phdrRdr = dataRdr.getRangeAsNewReader(dataRdr.readUint(4));
    _log(dataRdr.readString(4));
    this._pbagRdr = dataRdr.getRangeAsNewReader(dataRdr.readUint(4));
    _log(dataRdr.readString(4));
    this._pmodRdr = dataRdr.getRangeAsNewReader(dataRdr.readUint(4));
    _log(dataRdr.readString(4));
    this._pgenRdr = dataRdr.getRangeAsNewReader(dataRdr.readUint(4));
    _log(dataRdr.readString(4));
    this._instRdr = dataRdr.getRangeAsNewReader(dataRdr.readUint(4));
    _log(dataRdr.readString(4));
    this._ibagRdr = dataRdr.getRangeAsNewReader(dataRdr.readUint(4));
    _log(dataRdr.readString(4));
    this._imodRdr = dataRdr.getRangeAsNewReader(dataRdr.readUint(4));
    _log(dataRdr.readString(4));
    this._igenRdr = dataRdr.getRangeAsNewReader(dataRdr.readUint(4));
    _log(dataRdr.readString(4));
    this._shdrRdr = dataRdr.getRangeAsNewReader(dataRdr.readUint(4));
  }
  async _createSamples() {
    let sampleRateConv = 1;
    this._samples = [];
    while (this._shdrRdr.position < this._shdrRdr.length - 46) {
      let cSample = new _Sf2Sample();
      cSample.name = this._shdrRdr.readString(20);
      cSample.start = Math.trunc(this._shdrRdr.readUint(4) * sampleRateConv);
      cSample.end = Math.trunc(this._shdrRdr.readUint(4) * sampleRateConv);
      cSample.loopStart = Math.trunc(this._shdrRdr.readUint(4) * sampleRateConv);
      cSample.loopEnd = Math.trunc(this._shdrRdr.readUint(4) * sampleRateConv);
      cSample.sampleRate = this._shdrRdr.readUint(4);
      cSample.originalPitch = this._shdrRdr.readUint(1);
      cSample.pitchCorrection = this._shdrRdr.readInt(1);
      cSample.sampleLink = this._shdrRdr.readUint(2);
      cSample.sampleType = this._shdrRdr.readUint(2);
      if (this._audioIsCompressed) {
        await this._addSF3AudioDataToSample(cSample);
      } else
        this._addSF2AudioDataToSample(cSample);
      this._samples.push(cSample);
      cSample.loopEnd -= cSample.start;
      cSample.loopStart -= cSample.start;
      cSample.end -= cSample.start;
      cSample.start = 0;
    }
  }
  async _addSF3AudioDataToSample(cSample) {
    let oggsIndex = this._samplesSectionByteIndex + 8 + cSample.start;
    let oggEndIndex = this._samplesSectionByteIndex + 8 + cSample.end;
    let oggData = new Int8Array(this._sfData).subarray(
      oggsIndex,
      oggEndIndex
    );
    let samplesArray = oggData.slice();
    let ab = samplesArray.buffer;
    cSample.audioBuffer = await this._context.decodeAudioData(ab);
    let sampleRateConv = this._context.sampleRate / cSample.sampleRate;
    cSample.start = 0;
    cSample.end = cSample.audioBuffer.length;
    cSample.loopStart = Math.trunc(sampleRateConv * cSample.loopStart);
    cSample.loopEnd = Math.trunc(sampleRateConv * cSample.loopEnd);
  }
  _addSF2AudioDataToSample(cSample) {
    let sByteStartPosition = 2 * cSample.start;
    let numSamples = cSample.end - cSample.start;
    let buffer = this._context.createBuffer(
      1,
      numSamples,
      cSample.sampleRate
    );
    let adjSampleStart = this._samplesSectionByteIndex + sByteStartPosition;
    let sdata = new Int16Array(this._sfData, adjSampleStart, numSamples);
    let channel = buffer.getChannelData(0);
    for (let i = 0; i < sdata.length; i++) {
      channel[i] = sdata[i] / 32768;
    }
    cSample.audioBuffer = buffer;
  }
  // Promise _pause(){
  // 	let completer = new Completer();
  // 	Timer.run(()=> completer.complete());
  // 	return completer.future;
  // }
  // void _testSound(AudioBuffer buffer){
  // 	let bs = _context.createBufferSource();
  // 	bs.buffer = buffer;
  // 	bs.connectNode(_context.destination);
  // 	bs.start(0);
  // }
  // void _testSampleSound(Sample sample){
  // 	let bs = _context.createBufferSource();
  // 	let s = sample;
  // 	bs.buffer = s.audioBuffer;
  // 	bs.loop = s.loopStart > 0;
  // 	bs.loopStart = (s.loopStart) / s.sampleRate;
  // 	bs.loopEnd = (s.loopEnd) / s.sampleRate;
  // 	bs.connectNode(_context.destination);
  // 	bs.start(0);
  // 	print('test pitch: ${s.name}');
  // }
  _createInstruments() {
    let cInstrument;
    this._instruments = [];
    while (this._instRdr.position < this._instRdr.length) {
      cInstrument = new _Sf2Instrument();
      cInstrument.name = this._instRdr.readString(20).trim();
      this._instRdr.readUint(2);
      if (cInstrument.name !== "EOI" && cInstrument.name !== "") {
        this._instruments.push(cInstrument);
      } else {
        break;
      }
      this._instRdr.position += 20;
      let nextInstrumentsBagStartIndex = this._instRdr.readUint(2);
      this._instRdr.position -= 22;
      while (this._ibagRdr.position < 4 * nextInstrumentsBagStartIndex) {
        let cZone;
        if (cInstrument.zones.length > 0) {
          cZone = cInstrument.zones[0].isGlobal ? cInstrument.zones[0].clone() : _Sf2Zone.createForInstrument();
        } else {
          cZone = _Sf2Zone.createForInstrument();
        }
        cInstrument.zones.push(cZone);
        this._ibagRdr.readUint(2);
        this._ibagRdr.readUint(2);
        let nextZonesGenStartIndex = this._ibagRdr.readUint(2);
        let nextZonesModStartIndex = this._ibagRdr.readUint(2);
        this._ibagRdr.position -= 4;
        while (this._igenRdr.position < 4 * nextZonesGenStartIndex) {
          let genEnum = this._igenRdr.readUint(2);
          let value;
          switch (genEnum) {
            case 2:
              cZone.startLoopAddrsOffset = this._igenRdr.readInt(2);
              break;
            case 3:
              cZone.endLoopAddrsOffset = this._igenRdr.readInt(2);
              break;
            case 8:
              cZone.initialFilterFc = this._igenRdr.readInt(2);
              break;
            case 11:
              cZone.modEnvToFilterFc = this._igenRdr.readInt(2);
              break;
            case 13:
              value = this._igenRdr.readInt(2);
              break;
            case 16:
              value = this._igenRdr.readInt(2);
              break;
            case 17:
              value = this._igenRdr.readInt(2);
              break;
            case 22:
              value = this._igenRdr.readInt(2);
              break;
            case 24:
              value = this._igenRdr.readInt(2);
              break;
            case 26:
              value = this._igenRdr.readInt(2);
              break;
            case 29:
              value = this._igenRdr.readInt(2);
              break;
            case 33:
              value = this._igenRdr.readInt(2);
              cZone.delayVolEnvTime = Math.pow(2, value / 1200);
              break;
            case 34:
              value = this._igenRdr.readInt(2);
              cZone.attackVolEnvTime = Math.pow(2, value / 1200);
              break;
            case 35:
              value = this._igenRdr.readInt(2);
              cZone.holdVolEnvTime = Math.pow(2, value / 1200);
              break;
            case 36:
              value = this._igenRdr.readInt(2);
              cZone.decayVolEnvTime = Math.pow(2, value / 1200);
              break;
            case 37:
              value = this._igenRdr.readInt(2);
              if (value > 1e3) {
                value = 1e3;
              } else if (value < 0) {
                value = 0;
              }
              cZone.sustainVolReduction = value / 1e3;
              break;
            case 38:
              value = this._igenRdr.readInt(2);
              cZone.releaseVolEnvTime = Math.pow(2, value / 1200);
              break;
            case 43:
              cZone.bottomNote = this._igenRdr.readInt(1);
              cZone.topNote = this._igenRdr.readInt(1);
              break;
            case 44:
              cZone.bottomVel = this._igenRdr.readInt(1);
              cZone.topVel = this._igenRdr.readInt(1);
              break;
            case 48:
              cZone.initialAttenuation = this._igenRdr.readInt(2);
              break;
            case 51:
              cZone.courseTune = this._igenRdr.readInt(2);
              break;
            case 52:
              cZone.fineTune = this._igenRdr.readInt(2) / 100;
              break;
            case 53:
              value = this._igenRdr.readUint(2);
              if (this._samples.length > value && value >= 0) {
                cZone.sample = this._samples[value];
              } else {
                console.log("no sample for me!");
              }
              break;
            case 54:
              let sampleMode = this._igenRdr.readUint(2);
              cZone.loop = sampleMode > 0;
              break;
            case 58:
              value = this._igenRdr.readInt(2);
              cZone.overridingRootKey = value;
              break;
            default:
              this._igenRdr.position += 2;
          }
        }
        while (this._imodRdr.position < 10 * nextZonesModStartIndex) {
          this._imodRdr.position += 10;
        }
        if (cZone.sample == null && cInstrument.zones.length === 1) {
          cZone.isGlobal = true;
        }
      }
    }
  }
  _createPresets() {
    if (this._presets == null)
      this._presets = [];
    let cPreset;
    let bytesRead = 0;
    while (bytesRead < this._phdrRdr.length) {
      cPreset = new _Sf2Preset();
      cPreset.name = this._phdrRdr.readString(20);
      cPreset.presetNumber = this._phdrRdr.readUint(2);
      cPreset.bankNumber = this._phdrRdr.readUint(2);
      cPreset.presetBagIndex = this._phdrRdr.readUint(2);
      cPreset.library = this._phdrRdr.readUint(4);
      cPreset.genre = this._phdrRdr.readUint(4);
      cPreset.morphology = this._phdrRdr.readUint(4);
      if (cPreset.name !== "EOP" && cPreset.name !== "") {
        this._presets.push(cPreset);
      } else {
        break;
      }
      this._phdrRdr.position += 24;
      let nextPresetBagStartIndex;
      try {
        nextPresetBagStartIndex = this._phdrRdr.readUint(2);
      } catch (e) {
        console.log(cPreset.name.charCodeAt(0));
        break;
      }
      this._phdrRdr.position -= 26;
      bytesRead += 38;
      while (this._pbagRdr.position < 4 * nextPresetBagStartIndex) {
        let cZone;
        if (cPreset.zones.length > 0) {
          cZone = cPreset.zones[0].isGlobal ? cPreset.zones[0].clone() : _Sf2Zone.createForPreset();
        } else {
          cZone = _Sf2Zone.createForPreset();
        }
        cPreset.zones.push(cZone);
        this._pbagRdr.readUint(2);
        this._pbagRdr.readUint(2);
        let nextZonesGenStartIndex = this._pbagRdr.readUint(2);
        let nextZonesModStartIndex = this._pbagRdr.readUint(2);
        this._pbagRdr.position -= 4;
        while (this._pgenRdr.position < 4 * nextZonesGenStartIndex) {
          let genEnum = this._pgenRdr.readUint(2);
          let value;
          switch (genEnum) {
            case 2:
              cZone.startLoopAddrsOffset = this._pgenRdr.readInt(2);
              break;
            case 3:
              cZone.endLoopAddrsOffset = this._pgenRdr.readInt(2);
              break;
            case 8:
              value = this._pgenRdr.readInt(2);
              cZone.initialFilterFc = Math.pow(2, value / 1200);
              break;
            case 11:
              cZone.modEnvToFilterFc = this._pgenRdr.readInt(2);
              break;
            case 13:
              value = this._pgenRdr.readInt(2);
              break;
            case 16:
              value = this._pgenRdr.readInt(2);
              break;
            case 17:
              value = this._pgenRdr.readInt(2);
              break;
            case 22:
              value = this._pgenRdr.readInt(2);
              break;
            case 24:
              value = this._pgenRdr.readInt(2);
              break;
            case 26:
              value = this._pgenRdr.readInt(2);
              break;
            case 29:
              value = this._pgenRdr.readInt(2);
              break;
            case 33:
              value = this._pgenRdr.readInt(2);
              cZone.delayVolEnvTime = Math.pow(2, value / 1200);
              break;
            case 34:
              value = this._pgenRdr.readInt(2);
              cZone.attackVolEnvTime = Math.pow(2, value / 1200);
              break;
            case 35:
              value = this._pgenRdr.readInt(2);
              cZone.holdVolEnvTime = Math.pow(2, value / 1200);
              break;
            case 36:
              value = this._pgenRdr.readInt(2);
              cZone.decayVolEnvTime = Math.pow(2, value / 1200);
              break;
            case 37:
              value = this._pgenRdr.readInt(2);
              if (value > 1e3) {
                value = 1e3;
              } else if (value < 0) {
                value = 0;
              }
              cZone.sustainVolReduction = value / 1e3;
              break;
            case 38:
              value = this._pgenRdr.readInt(2);
              cZone.releaseVolEnvTime = Math.pow(2, value / 1200);
              break;
            case 41:
              value = this._pgenRdr.readUint(2);
              if (value === -68) {
                console.log("here");
              }
              cZone.instrument = this._instruments[value];
              break;
            case 43:
              cZone.bottomNote = this._pgenRdr.readInt(1);
              cZone.topNote = this._pgenRdr.readInt(1);
              break;
            case 44:
              cZone.bottomVel = this._pgenRdr.readInt(1);
              cZone.topVel = this._pgenRdr.readInt(1);
              break;
            case 48:
              cZone.initialAttenuation = this._pgenRdr.readInt(2);
              break;
            case 51:
              cZone.courseTune = this._pgenRdr.readInt(2);
              break;
            case 52:
              cZone.fineTune = this._pgenRdr.readInt(2) / 100;
              break;
            case 54:
              let sampleMode = this._pgenRdr.readUint(2);
              cZone.loop = sampleMode > 0;
              break;
            case 58:
              value = this._pgenRdr.readInt(2);
              cZone.overridingRootKey = value;
              break;
            default:
              this._pgenRdr.position += 2;
          }
        }
        while (this._pmodRdr.position < 10 * nextZonesModStartIndex) {
          this._pmodRdr.position += 10;
        }
        if (cZone.instrument == null && cPreset.zones.length === 1) {
          cZone.isGlobal = true;
        }
      }
    }
  }
  get audioContext() {
    return this._context;
  }
};
var _BinaryListReader = class __BinaryListReader {
  constructor(arrayBuffer, offsetInBytes, length) {
    this._arrayBuffer = arrayBuffer;
    this._startIndex = offsetInBytes ?? 0;
    this._i = this._startIndex;
    this._length = length ?? this._arrayBuffer.byteLength - this._startIndex;
    this._byteDataView = new DataView(arrayBuffer, 0);
    this._uint8List = new Uint8Array(this._arrayBuffer);
    this._textDecoder = new TextDecoder();
  }
  /**reads a string from the list and moves the read position forward
   * by the numBytes*/
  readString(numBytes = 4) {
    let str = this._textDecoder.decode(this._uint8List.subarray(
      this._i,
      this._i + numBytes
    ));
    this._i += numBytes;
    return str.replace(/[\u{0}\u{7}\u{1}]/gu, "");
  }
  readInt(numBytes = 1) {
    let val;
    switch (numBytes) {
      case 4:
        val = this._byteDataView.getInt32(this._i, true);
        break;
      case 2:
        val = this._byteDataView.getInt16(this._i, true);
        break;
      case 1:
        val = this._byteDataView.getInt8(this._i);
        break;
      default:
        throw "unsupported number of bytes for integer: $numBytes";
    }
    this._i += numBytes;
    return val;
  }
  readUint(numBytes = 1) {
    let val;
    switch (numBytes) {
      case 4:
        val = this._byteDataView.getUint32(this._i, true);
        break;
      case 2:
        val = this._byteDataView.getUint16(this._i, true);
        break;
      case 1:
        val = this._byteDataView.getUint8(this._i);
        break;
      default:
        throw "unsupported number of bytes for integer: $numBytes";
    }
    this._i += numBytes;
    return val;
  }
  /**creates a new Uint8ListReader from a range selection of this reader's
   * list. The read position is moved forward by offset + length.*/
  getRangeAsNewReader(length, offset = 0) {
    this._i += offset;
    let newReaderStartIndex = this._i;
    this._i += length;
    return new __BinaryListReader(
      this._arrayBuffer,
      newReaderStartIndex,
      length
    );
  }
  /**gets the current read position*/
  get position() {
    return this._i - this._startIndex;
  }
  set position(value) {
    this._i = value + this._startIndex;
  }
  /**gets the total length of the list*/
  get length() {
    return this._length;
  }
};

// src/sound_lib/sfz/_SfzParser.ts
import { ArrayUtils, CompressionUtils as CompressionUtils2 } from "ts_utils";

// src/sound_lib/sfz/_SfzPreset.ts
var _SfzPreset = class extends _BasicPreset {
  constructor() {
    super(...arguments);
    this.regions = [];
    ///true if audio data has been decoded and loaded for all samples used by
    ///this preset.
    this.samplesLoaded = false;
  }
};

// src/sound_lib/sfz/_SfzRegion.ts
var _SfzRegion = class __SfzRegion {
  constructor() {
    ///////INPUT CONTROLS FOR TRIGGERING REGION/////////
    /**minimum MIDI key 0-127*/
    this.lokey = 0;
    /**maximum MIDI key 0-127*/
    this.hikey = 127;
    /**minimum velocity level 0-127*/
    this.lovel = 0;
    /**maximum velocity level 0-127*/
    this.hivel = 127;
    /**minimum MIDI channel 1-16*/
    this.lochan = 1;
    /**maximum MIDI channel 1-16*/
    this.hichan = 16;
    /**/
    ///PERFORMANCE PARAMETERS////////
    ///start time, in samples
    this.offset = 0;
    /**end time, in samples. If -1, sample will not play. If 0 (default), entire 
     * sample will play.*/
    this.end = 0;
    /**loop start time, in samples.*/
    this.loopStart = 0;
    /**loop end time, in samples.*/
    this.loopEnd = 0;
    /**the loop behavior*/
    this.loopMode = 0 /* noLoop */;
    /**root key for the sample (60 == C4)*/
    this.pitchKeycenter = 60;
    /**defines how much the pitch changes with every note in relation to
     * [pitchKeycenter]. -1200 to 1200 cents. Defaults to 100*/
    this.pitchKeytrack = 100;
    /**fine tuning, in cents (-100 to 100)*/
    this.tune = 0;
    /**transposition in semitones*/
    this.transpose = 0;
    /**volume in decibels. Max in SFZ is 6, which would result in 2.0 with scale
     * calculation below. But converted SF2 files will likely have max of 0, 
     * which converts to 1.0.
     * use 10^(dbVolume/20) to convert to 0.0-1.0 percent scale*/
    this.dbVolume = 0;
    /**duration, in seconds, before note attack starts*/
    this.ampegDelay = 0;
    /**start volume (0.0-1.0) (before attack ramp-up)*/
    this.ampegStart = 0;
    /**duration, in seconds, of attack ramp-up*/
    this.ampegAttack = 0;
    //duration, in seconds, of steady hold at max percentage
    this.ampegHold = 0;
    //duration, in seconds, of decay in volume percentage to sustain level
    this.ampegDecay = 0;
    /**sustain volume (0.0-1.0) (after decay and until note release)*/
    this.ampegSustain = 1;
    /**duration, in seconds, of fall to 0 volume percentage after note release*/
    this.ampegRelease = 0;
    /**Filter type*/
    this.filType = 0 /* lpf1p */;
    /**The filter cutoff resonance value, in decibels.*/
    this.resonance = 0;
    /**duration, in seconds, before filter attack starts*/
    this.filegDelay = 0;
    /**start filter % (0.0-1.0) (before attack ramp-up)*/
    this.filegStart = 0;
    /**duration, in seconds, of attack ramp-up phase for filter*/
    this.filegAttack = 0;
    //duration, in seconds, of steady hold at max percentage for filter
    this.filegHold = 0;
    //duration, in seconds, of decay in filter percentage to sustain level
    this.filegDecay = 0;
    /**sustain filter % (0.0-1.0) (after decay and until note release)*/
    this.filegSustain = 1;
    /**duration, in seconds, of fall to 0 filter percentage after note release*/
    this.filegRelease = 0;
    /**depth for the filter EG, in cents (-12000 to 12000)*/
    this.filegDepth = 0;
  }
  clone() {
    let r = new __SfzRegion();
    r.sample = this.sample;
    r.lokey = this.lokey;
    r.hikey = this.hikey;
    r.lovel = this.lovel;
    r.hivel = this.hivel;
    r.lochan = this.lochan;
    r.hichan = this.hichan;
    r.offset = this.offset;
    r.end = this.end;
    r.loopStart = this.loopStart;
    r.loopEnd = this.loopEnd;
    r.loopMode = this.loopMode;
    r.pitchKeycenter = this.pitchKeycenter;
    r.pitchKeytrack = this.pitchKeytrack;
    r.tune = this.tune;
    r.transpose = this.transpose;
    r.ampegDelay = this.ampegDelay;
    r.ampegStart = this.ampegStart;
    r.ampegAttack = this.ampegAttack;
    r.ampegHold = this.ampegHold;
    r.ampegDecay = this.ampegDecay;
    r.ampegSustain = this.ampegSustain;
    r.ampegRelease = this.ampegRelease;
    r.filType = this.filType;
    r.cutoff = this.cutoff;
    r.resonance = this.resonance;
    r.filegDelay = this.filegDelay;
    r.filegStart = this.filegStart;
    r.filegAttack = this.filegAttack;
    r.filegHold = this.filegHold;
    r.filegDecay = this.filegDecay;
    r.filegSustain = this.filegSustain;
    r.filegRelease = this.filegRelease;
    r.filegDepth = this.filegDepth;
    return r;
  }
  setLoopModeFromSfzValue(sfzLoopValue) {
    switch (sfzLoopValue) {
      case "no_loop":
        this.loopMode = 0 /* noLoop */;
        return;
      case "one_shot":
        this.loopMode = 1 /* oneShot */;
        return;
      case "loop_continuous":
        this.loopMode = 2 /* loopContinuous */;
        return;
      case "loop_sustain":
        this.loopMode = 3 /* loopSustain */;
        return;
      default:
        console.log(`unknown loop mode: ${sfzLoopValue}`);
    }
  }
  setFilterTypeFromSfzValue(sfzLoopValue) {
    switch (sfzLoopValue) {
      case "lpf_1p":
        this.filType = 0 /* lpf1p */;
        return;
      case "hpf_1p":
        this.filType = 1 /* hpf1p */;
        return;
      case "lpf_2p":
        this.filType = 2 /* lpf2p */;
        return;
      case "hpf_2p":
        this.filType = 3 /* hpf2p */;
        return;
      case "bpf_2p":
        this.filType = 4 /* bpf2p */;
        return;
      case "brf_2p":
        this.filType = 5 /* brf2p */;
        return;
    }
  }
};

// src/sound_lib/sfz/_SfzSample.ts
import { CompressionUtils, ConversionUtils } from "ts_utils";
var _SfzSample = class {
  async loadAudio(context) {
    if (this._buffer != null)
      return;
    try {
      let blob = await CompressionUtils.getBlobFromEntry(this._zipEntry);
      let ab = await ConversionUtils.getArrayBufferFromBinaryBlob(blob);
      this._getSampleRate(ab);
      this._buffer = await context.decodeAudioData(ab);
    } catch (err) {
      console.log(err);
    }
  }
  _getSampleRate(arrayBuffer) {
    if (this._path.endsWith(".ogg")) {
      let dataView = new DataView(arrayBuffer, 40, 4);
      this._sampleRate = dataView.getInt32(0, true);
    } else if (this._path.endsWith(".wav")) {
      let dataView = new DataView(arrayBuffer, 24, 4);
      this._sampleRate = dataView.getInt32(0, true);
    }
  }
};

// src/sound_lib/sfz/_SfzParser.ts
var _SfzParser = class {
  constructor() {
    this._presets = [];
    this._samples = [];
  }
  /**/
  ////////////////////////////////////*/
  _SfzParser() {
  }
  /**Performs initial unpacking of Sfz.zip file, creating initial preset and 
   * sample objects. Instruments are not completely parsed or loaded to save 
   * processing. Use [parseAndLoadAllPresets] or [parseAndLoadPreset] to load 
   * instruments for playback.*/
  async initSfz(sfzZip) {
    let entries = await CompressionUtils2.unzip(sfzZip);
    await this._getPresetsAndSamplesFromEntries(entries);
  }
  async parseAndLoadAllPresets() {
    for (let preset of this._presets) {
      await this.parseAndLoadPreset(preset);
    }
  }
  async parseAndLoadPreset(preset) {
    if (preset.samplesLoaded)
      return;
    this._parsePreset(preset);
    await this._loadSamples(preset);
  }
  getPreset(bank, presetNumber) {
    return ArrayUtils.lastWhere(this._presets, (p) => {
      return p.bankNumber === bank && p.presetNumber === presetNumber;
    });
  }
  async _getPresetsAndSamplesFromEntries(entries) {
    entries.sort((e1, e2) => e1.filename.localeCompare(e2.filename));
    let currentBank = 0;
    for (let e of entries) {
      if (e.directory) {
        let path = e.filename;
        if (path.length >= 5) {
          let end = path.substring(path.length - 5);
          if (/\/\d\d\d\//.test(end)) {
            currentBank = Number.parseInt(end.substring(1, 4));
          }
        }
      } else if (e.filename.endsWith(".wav") || e.filename.endsWith("ogg") || e.filename.endsWith(".mp3")) {
        var sample = new _SfzSample();
        sample._path = e.filename;
        sample._zipEntry = e;
        this._samples.push(sample);
      } else if (e.filename.endsWith(".sfz")) {
        let sfz = await CompressionUtils2.getStringFromEntry(e);
        var preset = new _SfzPreset();
        preset.sfz = sfz;
        preset.bankNumber = currentBank;
        let index = e.filename.lastIndexOf("/") + 1;
        preset.presetNumber = Number.parseInt(e.filename.substring(index, index + 3));
        this._presets.push(preset);
      }
    }
  }
  _parsePreset(preset) {
    this._currentPreset = preset;
    let data = preset.sfz.replace(/\/\/.*/g, "");
    let lines = data.split(/[\n\r]/);
    for (let line of lines) {
      let i = 0;
      let length = line.length;
      while (i < length) {
        let char = line[i];
        if (char === " " || char === "	") {
          i++;
          continue;
        } else if (char === "<") {
          let endIndex = this._processHeader(line, i);
          if (endIndex === -1) {
            break;
          }
          i = endIndex + 1;
        } else {
          let endIndex = this._processOpcode(line, i);
          if (endIndex === -1) {
            break;
          }
          i = endIndex + 1;
        }
      }
    }
  }
  _processHeader(line, startIndex) {
    let endIndex = line.indexOf(">", startIndex + 1);
    if (endIndex === -1)
      return -1;
    let header = line.substring(startIndex + 1, endIndex);
    switch (header) {
      case "global":
        this._global = new _SfzRegion();
        this._group = null;
        this._region = null;
        break;
      case "group":
        this._group = this._global != null ? this._global.clone() : new _SfzRegion();
        this._region = null;
        break;
      case "region":
        if (this._group != null) {
          this._region = this._group.clone();
        } else if (this._global != null) {
          this._region = this._global.clone();
        } else {
          this._region = new _SfzRegion();
        }
        this._currentPreset.regions.push(this._region);
        break;
      default:
        console.log("unsupported header: $header");
    }
    return endIndex;
  }
  _processOpcode(line, startIndex) {
    let equalsIndex = line.indexOf("=", startIndex);
    if (equalsIndex === -1 || equalsIndex >= line.length - 1) {
      return -1;
    }
    let reg = this._region;
    if (reg == null) {
      reg = this._group || this._global;
    }
    let opcode = line.substring(startIndex, equalsIndex);
    let value = line.substring(equalsIndex + 1);
    let endIndex = line.length - 1;
    if (opcode !== "sample") {
      let spaceIndex = value.search(/\s/);
      if (spaceIndex > -1 && spaceIndex < value.length - 1) {
        value = value.substring(0, spaceIndex);
        endIndex = equalsIndex + 1 + spaceIndex;
      }
    }
    switch (opcode) {
      case "sample":
        value = value.replace(/\\/g, "/");
        let index = value.lastIndexOf("../");
        index = index > -1 ? index + 3 : 0;
        let filePath = value.substring(index, value.length - 3);
        let sample = this._samples.find((s) => {
          return s._path.includes(filePath);
        });
        if (sample != null)
          reg.sample = sample;
        break;
      case "lokey":
        reg.lokey = Number.parseInt(value);
        break;
      case "hikey":
        reg.hikey = Number.parseInt(value);
        break;
      case "lovel":
        reg.lovel = Number.parseInt(value);
        break;
      case "hivel":
        reg.hivel = Number.parseInt(value);
        break;
      case "lochan":
        reg.lochan = Number.parseInt(value);
        break;
      case "hichan":
        reg.hichan = Number.parseInt(value);
        break;
      case "group":
        break;
      case "off_by":
        break;
      case "offset":
        reg.offset = Number.parseInt(value);
        break;
      case "end":
        reg.end = Number.parseInt(value);
        break;
      case "loop_start":
        reg.loopStart = Number.parseInt(value);
        break;
      case "loop_end":
        reg.loopEnd = Number.parseInt(value) + 1;
        break;
      case "loop_mode":
        reg.setLoopModeFromSfzValue(value);
        break;
      case "pitch_keycenter":
        reg.pitchKeycenter = Number.parseInt(value);
        break;
      case "pitch_keytrack":
        reg.pitchKeytrack = Number.parseInt(value);
        break;
      case "tune":
        reg.tune = Number.parseInt(value);
        break;
      case "transpose":
        reg.transpose = Number.parseInt(value);
        break;
      case "ampeg_delay":
        reg.ampegDelay = Number.parseFloat(value);
        break;
      case "ampeg_start":
        reg.ampegStart = Number.parseFloat(value) / 100;
        break;
      case "ampeg_attack":
        reg.ampegAttack = Number.parseFloat(value);
        break;
      case "ampeg_hold":
        reg.ampegHold = Number.parseFloat(value);
        break;
      case "ampeg_decay":
        reg.ampegDecay = Number.parseFloat(value);
        break;
      case "ampeg_sustain":
        reg.ampegSustain = Number.parseFloat(value) / 100;
        break;
      case "ampeg_release":
        reg.ampegRelease = Number.parseFloat(value);
        break;
      case "ampeg_holdcc133":
        break;
      case "ampeg_decaycc133":
        break;
      case "pitcheg_delay":
        break;
      case "pitcheg_attack":
        break;
      case "pitcheg_hold":
        break;
      case "pitcheg_decay":
        break;
      case "pitcheg_sustain":
        break;
      case "pitcheg_release":
        break;
      case "pitcheg_depth":
        break;
      case "pitcheg_holdcc133":
        break;
      case "pitcheg_decaycc133":
        break;
      case "fileg_delay":
        reg.filegDelay = Number.parseFloat(value);
        break;
      case "fileg_start":
        reg.filegStart = Number.parseFloat(value) / 100;
        break;
      case "fileg_attack":
        reg.filegAttack = Number.parseFloat(value);
        break;
      case "fileg_hold":
        reg.filegHold = Number.parseFloat(value);
        break;
      case "fileg_decay":
        reg.filegDecay = Number.parseFloat(value);
        break;
      case "fileg_sustain":
        reg.filegSustain = Number.parseFloat(value) / 100;
        break;
      case "fileg_release":
        reg.filegRelease = Number.parseFloat(value);
        break;
      case "fileg_depth":
        reg.filegDepth = Number.parseInt(value);
        break;
      case "fileg_holdcc133":
        break;
      case "fileg_decaycc133":
        break;
      case "fil_type":
        reg.setFilterTypeFromSfzValue(value);
        break;
      case "cutoff":
        reg.cutoff = Number.parseFloat(value);
        break;
      case "resonance":
        reg.resonance = Number.parseFloat(value);
        break;
      case "volume":
        reg.dbVolume = Number.parseFloat(value);
        break;
      case "pan":
        break;
      case "amplfo_delay":
        break;
      case "amplfo_freq":
        break;
      case "amplfo_depth":
        break;
      case "fillfo_delay":
        break;
      case "fillfo_freq":
        break;
      case "fillfo_depth":
        break;
      case "pitchlfo_delay":
        break;
      case "pitchlfo_freq":
        break;
      case "pitchlfo_depth":
        break;
      case "effect1":
        break;
      case "effect2":
        break;
      default:
        console.log("unsupported opcode: $opcode");
    }
    return endIndex;
  }
  async _loadSamples(preset) {
    if (preset.samplesLoaded)
      return;
    let context = SoundLibManager.get().context;
    for (let reg of preset.regions) {
      await reg.sample.loadAudio(context);
    }
    preset.samplesLoaded = true;
  }
};

// src/sound_lib/core/SoundLibEvent.ts
var SoundLibEvent = class {
  constructor(type) {
    this.type = type;
  }
};
var SoundLibEventType = /* @__PURE__ */ ((SoundLibEventType2) => {
  SoundLibEventType2[SoundLibEventType2["loadingSoundMap"] = 0] = "loadingSoundMap";
  SoundLibEventType2[SoundLibEventType2["soundMapLoaded"] = 1] = "soundMapLoaded";
  SoundLibEventType2[SoundLibEventType2["loadProgress"] = 2] = "loadProgress";
  SoundLibEventType2[SoundLibEventType2["parsing"] = 3] = "parsing";
  SoundLibEventType2[SoundLibEventType2["libraryReady"] = 4] = "libraryReady";
  SoundLibEventType2[SoundLibEventType2["playbackFinished"] = 5] = "playbackFinished";
  SoundLibEventType2[SoundLibEventType2["noteSetComplete"] = 6] = "noteSetComplete";
  return SoundLibEventType2;
})(SoundLibEventType || {});

// src/sound_lib/core/SoundLibType.ts
var SoundLibType = /* @__PURE__ */ ((SoundLibType2) => {
  SoundLibType2[SoundLibType2["SF"] = 0] = "SF";
  SoundLibType2[SoundLibType2["SFZ"] = 1] = "SFZ";
  return SoundLibType2;
})(SoundLibType || {});

// src/sound_lib/core/_SoundMapFileEntry.ts
var _SoundMapFileEntry = class {
  constructor() {
    this._loaded = false;
    this._presets = [];
  }
};

// src/sound_lib/core/_SoundMapPresetEntry.ts
var _SoundMapPresetEntry = class {
};

// src/sound_lib/core/_SoundMap.ts
var _SoundMap = class {
  /**Constructs a new [_SoundMap]. The [_mapUrl] should be the path to the
   * json map file. The [json] should be formatted as follows:
   * ```
   * {
   * 	"id": "pianosDrums", "type": "sfz", "files":
   * 	[
   * 		{ "name": "pianos_ogg.zip", "version": 1, "presets": 
   * 			[
   * 				{ "bank": 0, "preset": 0 },
   * 				{ "bank": 0, "preset": 1 }
   * 			]
   * 		},
   * 		{ "name": "drums_ogg.zip", "version": 1, "presets": 
   * 			[
   * 				{ "bank": 128, "preset": 0 }
   * 			]
   * 		}
   * 	]
   * }
   * ```
   * The id should be unique so it can act as a db prefix for files.
   * Each file entry in the json points to a .zip file or .sf2/.sf3 file. In
   * the case of .zip files for SFZ libraries, the folder structure should
   * place presets in folders named by their bank numbers. Samples can be
   * in a folder anywhere, but typically the samples folder will be at the
   * top level of the folder. SFZ folder/file example for map above: 
   * ```
   * [sfzMap]
   * 	[map.json]
   * 	[pianos_ogg.zip]
   * 		[000]
   * 			[000_Stereo Grand.sfz]
   * 			[001_Bright Grand.sfz]
   * 		[samples]
   * 			[Grand Piano-A#1.ogg]
   * 			...
   * 	[drums_ogg.zip]
   * 		[128]
   * 			[000_Standard+Orch.sfz]
   * 		[samples]
   * 			[Agogo.ogg]
   * 			...
   * ```
   * 
   * SF example:
   * ```
   * [sf2Map]
   * 	[map.json]
   * 	[pianos.sf2]
   * 	[drums.sf2]
   * ```
   * */
  constructor(mapUrl, json) {
    /**package internal use */
    this._fileEntries = [];
    this._mapUrl = mapUrl;
    try {
      this._id = json["id"];
      if (!this._id) {
        throw "a unique sound map id must be provided!";
      }
      let libType = json["type"].toLowerCase();
      if (libType === "sfz") {
        this._type = 1 /* SFZ */;
      } else if (libType === "sf") {
        this._type = 0 /* SF */;
      } else {
        this._isValid = false;
        throw "type in sound lib json must be sf or sfz";
      }
      for (let fileJson of json["files"]) {
        let fileEntry = new _SoundMapFileEntry();
        fileEntry._name = fileJson["name"];
        fileEntry._version = fileJson["version"];
        for (let presetJson of fileJson["presets"]) {
          let preset = new _SoundMapPresetEntry();
          preset._bank = presetJson["bank"];
          preset._preset = presetJson["preset"];
          fileEntry._presets.push(preset);
        }
        this._fileEntries.push(fileEntry);
      }
      this._isValid = true;
    } catch (err) {
      console.log(err);
      this._isValid = false;
    }
  }
  /**Gets the first [_SoundMapFileEntry] containing a preset that matches the
   * requested [bank] and [preset]. If none is found, null is returned;*/
  _getFileMatchingPreset(bank, preset) {
    for (let fileEntry of this._fileEntries) {
      for (let presetEntry of fileEntry._presets) {
        if (presetEntry._bank === bank && presetEntry._preset === preset) {
          return fileEntry;
        }
      }
    }
    return null;
  }
};

// src/sound_lib/core/_SoundLibLoader.ts
var _SoundLibLoader = class {
  constructor() {
    this._idbConfig = {
      "dbName": "",
      //must be provided via _dbName
      "version": 1,
      "deleteOldVersionStores": true,
      "objectStores": [
        {
          "name": "soundlibfiles",
          "keypath": "id",
          "indexes": [
            {
              "name": "filename",
              "keypath": "filename",
              "unique": false
            },
            {
              "name": "version",
              "keypath": "version",
              "unique": false
            },
            {
              "name": "blob",
              "keypath": "blob",
              "unique": false
            }
          ]
        }
      ]
    };
    this._slm = SoundLibManager.get();
    this._init();
  }
  async loadSoundMap(path) {
    let ev = new SoundLibEvent(0 /* loadingSoundMap */);
    this._slm._dispatch(ev);
    try {
      let jsonString = await HttpUtils.getString(path);
      let json = JsonUtils.decode(jsonString);
      this._soundMap = new _SoundMap(path, json);
      if (this._soundMap._isValid) {
        if (this._slm._libType === this._soundMap._type) {
          this._slm._dispatch(new SoundLibEvent(
            1 /* soundMapLoaded */
          ));
        } else {
          throw "sound lib json type does not match SoundLibManager";
        }
      } else {
        throw "invalid sound map file";
      }
    } catch (err) {
      console.log(err.toString());
    }
  }
  async loadLibrary(path, loadSfzPresets = true, fileVersion = null) {
    this._checkType(path);
    let bb = await this._getSfxFile(path, fileVersion);
    this._slm._dispatch(new SoundLibEvent(3 /* parsing */));
    if (this._slm._libType === 1 /* SFZ */) {
      await this._parser.initSfz(bb);
      if (loadSfzPresets) {
        await this._parser.parseAndLoadAllPresets();
      }
    } else if (this._slm._libType === 0 /* SF */) {
      let compressed = path.endsWith(".sf3") || path.endsWith(".sf4");
      await this._parser.parse(bb, compressed);
    }
    this._slm._dispatch(new SoundLibEvent(4 /* libraryReady */));
  }
  async loadPresets(bankPresetPairs) {
    let mapDir;
    if (this._soundMap != null) {
      mapDir = BrowserUtils.getFolderPathToFile(this._soundMap._mapUrl);
    }
    for (let i = 0; i < bankPresetPairs.length - 1; i += 2) {
      let bankNum = bankPresetPairs[i];
      let presetNum = bankPresetPairs[i + 1];
      let preset = this._parser.getPreset(bankNum, presetNum);
      if (preset != null) {
        if (this._slm._libType === 1 /* SFZ */) {
          if (!preset.samplesLoaded) {
            await this._parser.parseAndLoadPreset(preset);
          }
        }
      } else {
        let fileEntry = this._soundMap?._getFileMatchingPreset(
          bankNum,
          presetNum
        );
        if (fileEntry != null) {
          await this.loadLibrary(
            `${mapDir}${fileEntry._name}`,
            true,
            fileEntry._version
          );
          fileEntry._loaded = true;
        } else {
          throw `bank: ${bankNum}, preset: ${presetNum} is not available`;
        }
      }
    }
  }
  async loadAllPresets() {
    if (this._slm._libType === 1 /* SFZ */) {
      let presets = this._parser._presets;
      for (let p of presets) {
        if (!p.samplesLoaded) {
          await this._parser.parseAndLoadPreset(p);
        }
      }
    }
    if (this._soundMap != null) {
      let mapDir = BrowserUtils.getFolderPathToFile(
        this._soundMap._mapUrl
      );
      for (let fileEntry of this._soundMap._fileEntries) {
        if (!fileEntry._loaded) {
          await this.loadLibrary(
            `${mapDir}${fileEntry._name}`,
            true,
            fileEntry._version
          );
          fileEntry._loaded = true;
        }
      }
    }
  }
  hasPreset(bankNumber, presetNumber) {
    return this._parser.getPreset(bankNumber, presetNumber) != null;
  }
  getPreset(bankNumber, presetNumber) {
    return this._parser.getPreset(bankNumber, presetNumber);
  }
  _init() {
    if (this._slm._libType === 0 /* SF */) {
      this._parser = new _Sf2Parser(this._slm.context);
    } else if (this._slm._libType === 1 /* SFZ */) {
      this._parser = new _SfzParser();
    }
  }
  async _getSfxFile(path, fileVersion) {
    let progressCallback;
    SoundLoader.downloadProgress.on(progressCallback = (e) => {
      let event = new SoundLibEvent(2 /* loadProgress */);
      event.progress = e;
      this._slm._dispatch(event);
    });
    let ab;
    if (this._dbName != null && IdxDbManager.isSupported) {
      ab = await this._loadFromDb(path, fileVersion);
    } else {
      ab = await this._downloadArrayBuffer(path);
    }
    SoundLoader.downloadProgress.off(progressCallback);
    return ab;
  }
  _checkType(libPath) {
    if (this._slm._libType === 1 /* SFZ */) {
      if (!libPath.endsWith(".zip")) {
        throw "Invalid SFZ file name - must end in .zip";
      }
    } else if (this._slm._libType === 0 /* SF */) {
      if (!libPath.endsWith(".sf2") && !libPath.endsWith(".sf3") && !libPath.endsWith(".sf4")) {
        throw "Invalid SF file name - must end in .sf2, .sf3, or .sf4";
      }
    }
  }
  async _downloadArrayBuffer(path) {
    return await SoundLoader.getArrayBufferFromUrl(path);
  }
  async _loadFromDb(path, fileVersion) {
    if (this._idbManager == null) {
      this._idbConfig["dbName"] = this._dbName;
      this._idbManager = new IdxDbManager(this._idbConfig);
    }
    try {
      let response = await this._idbManager.open();
      if (!response.success) {
        return await this._downloadArrayBuffer(path);
      }
      let blob;
      let key = `${this._soundMap._id}_${path}`;
      response = await this._idbManager.getRecord("soundlibfiles", key);
      if (!response.success) {
        console.log(response.error);
        return await this._downloadArrayBuffer(path);
      } else if (response.record == null) {
        let bb = await this._downloadArrayBuffer(path);
        blob = new Blob([bb]);
        await this._createRecord(blob, key, fileVersion);
      } else if (response.record["version"] !== fileVersion) {
        let bb = await this._downloadArrayBuffer(path);
        blob = new Blob([bb]);
        await this._updateRecord(blob, response.record, fileVersion);
      } else {
        blob = response.record["blob"];
      }
      return await ConversionUtils2.getArrayBufferFromBinaryBlob(blob);
    } catch (err) {
      console.log(err);
      return await this._downloadArrayBuffer(path);
    }
  }
  async _createRecord(blob, key, version) {
    let record = {
      "id": key,
      "filename": key,
      "version": version,
      "blob": blob
    };
    await this._idbManager.addRecord("soundlibfiles", record);
  }
  async _updateRecord(blob, record, version) {
    record["blob"] = blob;
    record["version"] = version;
    await this._idbManager.updateRecord("soundlibfiles", record);
  }
};

// src/sound_lib/playback/PerformanceNoteDO.ts
var PerformanceNoteDO = class _PerformanceNoteDO {
  constructor() {
    /**in seconds, from the beginning of the performance*/
    this.startTime = 0;
    /**in seconds, from the beginning of the performance. The endTime includes
     * the release, so endTime - startTime will typically be > 
     * durationRequested*/
    this.endTime = 0;
    /**the start position of this note's sample data in the loaded SoundFont
     * audio file*/
    this.sfAudioStartPos = 0;
    /**the end position of this note's sample data in the loaded SoundFont audio
     * file*/
    this.sfAudioEndPos = 0;
    this.position = 0;
    //int sampleRate = 44100;
    /**added 8/10/2017 - the factor the playback rate will need to be multiplied
     * by to transpose the sample's original pitch to the desired pitch*/
    this.pitchStretchRatio = 1;
    this.sampleDebt = 0;
    /**in samples. Not truncated to number, because player converts this to
     * seconds*/
    this.loopStartPos = 0;
    /**in samples. Not truncated to number, because player converts this to
     * seconds*/
    this.loopEndPos = 0;
    this.leftVolume = 0;
    this.rightVolume = 0;
    this.fadeInSamples = 0;
    this.fadeOutSamples = 0;
    this.loop = false;
    //values used for envelope volume calculation
    /**the total amount of time the note has been played, in seconds -
     * does not reset with loop*/
    this.totalTimePlayed = 0;
    this.attackStartTime = 0;
    this.holdStartTime = 0;
    this.decayStartTime = 0;
    this.decayEndTime = 0;
    this.sustainVolReduction = 0;
    //0 to 1
    /**the volume we were at before the release phase started - must be tracked
     * in case a note ends before its envelopes finish*/
    this.preReleaseVol = 0;
    this.releaseStartTime = 0;
    this.amplitude = 1;
    this.pan = 0;
    /** if the player supports multiple mixing tracks, as in for per-track
     *  volume settings, the [trackId] associates the note with a track.*/
    this.trackId = 0;
  }
  /** creates one or more performance notes
   *  @param	midiNote 0-127 - 60 is middle C
   *  @param	startTime start time, in seconds
   *  @param	duration duration, in seconds
   *  @param	amplitude a value from 0 to 1 representing base volume level
   *  @param	pan a value from 0 to 1 representing pan (0 is all the way to
   * 	the left, 1 is all the way to the right)*/
  static createPerformanceNote(ndo, preset) {
    let notes = [];
    let midiVel = Math.round(ndo.amplitude * 127);
    for (let pZone of preset.zones) {
      if (pZone.bottomNote > ndo.midiNote || pZone.topNote < ndo.midiNote || pZone.instrument == null || pZone.bottomVel > midiVel || pZone.topVel < midiVel) {
        continue;
      }
      for (let iZone of pZone.instrument.zones) {
        if (iZone.bottomNote > ndo.midiNote || iZone.topNote < ndo.midiNote || iZone.sample == null || iZone.bottomVel > midiVel || iZone.topVel < midiVel) {
          continue;
        }
        notes.push(_PerformanceNoteDO._getNoteFromZones(
          pZone,
          iZone,
          ndo,
          preset
        ));
      }
    }
    return notes;
  }
  static _getNoteFromZones(pz, iz, ndo, preset) {
    let sample = iz.sample;
    let pNoteDO = new _PerformanceNoteDO();
    pNoteDO.preset = preset;
    pNoteDO.startTime = ndo.startTime;
    pNoteDO.endTime = ndo.startTime + ndo.duration;
    pNoteDO.durationRequested = ndo.duration;
    pNoteDO.audioBuffer = sample.audioBuffer;
    pNoteDO.sfAudioStartPos = sample.start;
    pNoteDO.sfAudioEndPos = sample.end;
    pNoteDO.position = pNoteDO.sfAudioStartPos;
    pNoteDO.trackId = ndo.trackId ?? 0;
    let sampleRootPitch = sample.originalPitch - sample.pitchCorrection / 100;
    if (pz.overridingRootKey > 0) {
      sampleRootPitch = pz.overridingRootKey;
    } else if (iz.overridingRootKey > 0) {
      sampleRootPitch = iz.overridingRootKey;
    }
    pNoteDO.pitchStretchRatio = Math.pow(2, (ndo.midiNote + iz.fineTune + pz.fineTune + iz.courseTune + pz.courseTune - sampleRootPitch) / 12);
    if (iz.loop) {
      pNoteDO.loopStartPos = iz.startLoopAddrsOffset + pz.startLoopAddrsOffset + sample.loopStart;
      pNoteDO.loopEndPos = iz.endLoopAddrsOffset + pz.endLoopAddrsOffset + sample.loopEnd;
    }
    pNoteDO.loop = iz.loop;
    let lowpassCents = iz.initialFilterFc + iz.modEnvToFilterFc + pz.modEnvToFilterFc;
    let delta = (lowpassCents - 6900) / 100;
    let lowpassFreq = Math.pow(2, delta / 12) * 440;
    lowpassFreq *= pz.initialFilterFc;
    if (lowpassFreq >= 20 && lowpassFreq <= 2e4) {
      pNoteDO.lowpassFreq = lowpassFreq;
      pNoteDO.filterMap = /* @__PURE__ */ new Map([[0, lowpassFreq]]);
    }
    let dbVelocityAmplitude = 20 * Math.log(ndo.amplitude);
    let dbPeakAmplitude = dbVelocityAmplitude - (iz.initialAttenuation + pz.initialAttenuation) / 10;
    let peakAmplitude = Math.pow(10, dbPeakAmplitude / 20);
    if (peakAmplitude > 1) {
      peakAmplitude = 1;
    }
    let fullDecayDur = iz.decayVolEnvTime * pz.decayVolEnvTime;
    let dbVolReduction = (iz.sustainVolReduction + pz.sustainVolReduction) * 100;
    let dbSustainLevel = -dbVolReduction;
    let releaseTime = iz.releaseVolEnvTime * pz.releaseVolEnvTime;
    let map = this._createTimeValueMap(
      0,
      pz.delayVolEnvTime * iz.delayVolEnvTime,
      0,
      pz.attackVolEnvTime * iz.attackVolEnvTime,
      peakAmplitude,
      pz.holdVolEnvTime * iz.holdVolEnvTime,
      fullDecayDur,
      Math.pow(10, dbSustainLevel / 20),
      Math.min(releaseTime, 4),
      ndo.duration,
      0
    );
    for (let k of map.keys()) {
      let v = map.get(k);
      if (v > 1 || v < 0 || Number.isNaN(v)) {
        console.log(`volume:${v}`);
      }
      if (k < 0 || k > 10) {
        console.log(`time:${k}`);
      }
    }
    pNoteDO.endTime = pNoteDO.startTime + ndo.duration + releaseTime;
    pNoteDO.volumeMap = map;
    pNoteDO.leftVolume = 2 * (1 - ndo.pan);
    pNoteDO.rightVolume = 2 * ndo.pan;
    if (pNoteDO.leftVolume > 1) {
      pNoteDO.leftVolume = 1;
    }
    if (pNoteDO.rightVolume > 1) {
      pNoteDO.rightVolume = 1;
    }
    pNoteDO.amplitude = ndo.amplitude;
    pNoteDO.pan = ndo.pan * 2 - 1;
    return pNoteDO;
  }
  clone() {
    let pdo = new _PerformanceNoteDO();
    pdo.durationRequested = this.durationRequested;
    pdo.startTime = this.startTime;
    pdo.endTime = this.endTime;
    pdo.audioBuffer = this.audioBuffer;
    pdo.sfAudioStartPos = this.sfAudioStartPos;
    pdo.sfAudioEndPos = this.sfAudioEndPos;
    pdo.position = this.position;
    pdo.pitchStretchRatio = this.pitchStretchRatio;
    pdo.sampleDebt = this.sampleDebt;
    pdo.loopStartPos = this.loopStartPos;
    pdo.loopEndPos = this.loopEndPos;
    pdo.leftVolume = this.leftVolume;
    pdo.rightVolume = this.rightVolume;
    pdo.fadeInSamples = this.fadeInSamples;
    pdo.fadeOutSamples = this.fadeOutSamples;
    pdo.loop = this.loop;
    pdo.lowpassFreq = this.lowpassFreq;
    pdo.filterMap = this.filterMap;
    pdo.volumeMap = this.volumeMap;
    pdo.totalTimePlayed = this.totalTimePlayed;
    pdo.attackStartTime = this.attackStartTime;
    pdo.holdStartTime = this.holdStartTime;
    pdo.decayStartTime = this.decayStartTime;
    pdo.decayEndTime = this.decayEndTime;
    pdo.sustainVolReduction = this.sustainVolReduction;
    pdo.preReleaseVol = this.preReleaseVol;
    pdo.releaseStartTime = this.releaseStartTime;
    pdo.amplitude = this.amplitude;
    pdo.pan = this.pan;
    pdo.preset = this.preset;
    pdo.trackId = this.trackId;
    return pdo;
  }
  /** creates one or more performance notes using an [_SfzPreset]*/
  static createNotesSfz(ndo, preset) {
    let notes = [];
    let vel = Math.round(ndo.amplitude * 127);
    for (let reg of preset.regions) {
      if (ndo.midiNote < reg.lokey || ndo.midiNote > reg.hikey || vel < reg.lovel || vel > reg.hivel) {
        continue;
      }
      let sample = reg.sample;
      let srRatio = sample._buffer.sampleRate / sample._sampleRate;
      let pdo = new _PerformanceNoteDO();
      pdo.sfzPreset = preset;
      pdo.startTime = ndo.startTime;
      pdo.endTime = ndo.startTime + ndo.duration;
      pdo.durationRequested = ndo.duration;
      pdo.audioBuffer = sample._buffer;
      pdo.sfAudioStartPos = Math.floor(reg.offset * srRatio);
      pdo.sfAudioEndPos = Math.floor(reg.end * srRatio);
      pdo.position = pdo.sfAudioStartPos;
      pdo.trackId = ndo.trackId ?? 0;
      let rootNote = reg.pitchKeycenter;
      let noteDelta = (ndo.midiNote - rootNote) * reg.pitchKeytrack / 100;
      noteDelta += reg.transpose + reg.tune / 100;
      pdo.pitchStretchRatio = Math.pow(2, noteDelta / 12);
      if (reg.loopMode === 2 /* loopContinuous */ || reg.loopMode === 3 /* loopSustain */) {
        pdo.loop = true;
        pdo.loopStartPos = reg.loopStart * srRatio;
        pdo.loopEndPos = reg.loopEnd * srRatio;
        if (pdo.loopEndPos >= sample._buffer.length) {
          pdo.loopEndPos = sample._buffer.length - 1;
        }
      }
      if (reg.cutoff != null) {
        if (reg.filType === 0 /* lpf1p */ || reg.filType === 2 /* lpf2p */) {
          pdo.lowpassFreq = reg.cutoff;
          let depth = reg.filegDepth / 1200;
          pdo.filterMap = this._createTimeValueMap(
            pdo.lowpassFreq * Math.pow(2, reg.filegStart * depth),
            reg.filegDelay,
            pdo.lowpassFreq * Math.pow(2, reg.filegStart * depth),
            reg.filegAttack,
            pdo.lowpassFreq * Math.pow(2, depth),
            reg.filegHold,
            reg.filegDecay,
            pdo.lowpassFreq * Math.pow(2, reg.filegSustain * depth),
            reg.filegRelease,
            ndo.duration,
            //final filter value ... make same as sustain?
            pdo.lowpassFreq * Math.pow(2, reg.filegSustain * depth)
          );
        }
      }
      let regVolAsPerc = (reg.dbVolume + 150.6) / 150.6;
      let peakAmplitude = ndo.amplitude * regVolAsPerc;
      if (peakAmplitude > 1)
        peakAmplitude = 1;
      let map = this._createTimeValueMap(
        0,
        reg.ampegDelay,
        reg.ampegStart * peakAmplitude,
        reg.ampegAttack,
        peakAmplitude,
        reg.ampegHold,
        reg.ampegDecay,
        reg.ampegSustain * peakAmplitude,
        reg.ampegRelease,
        ndo.duration,
        0
      );
      pdo.endTime = pdo.startTime + ndo.duration + reg.ampegRelease;
      pdo.volumeMap = map;
      pdo.leftVolume = 2 * (1 - ndo.pan);
      pdo.rightVolume = 2 * ndo.pan;
      if (pdo.leftVolume > 1) {
        pdo.leftVolume = 1;
      }
      if (pdo.rightVolume > 1) {
        pdo.rightVolume = 1;
      }
      pdo.amplitude = ndo.amplitude;
      pdo.pan = ndo.pan * 2 - 1;
      notes.push(pdo);
    }
    return notes;
  }
  static _createTimeValueMap(preDelayVal, delay, startVal, attack, peakVal, hold, decay, sustainVal, release, noteDuration, finalVal) {
    let envTime = 0;
    let map = /* @__PURE__ */ new Map([[envTime, preDelayVal]]);
    envTime += delay;
    if (envTime > 11e-5) {
      map.set(envTime - 1e-4, preDelayVal);
    }
    map.set(envTime, startVal);
    envTime += attack;
    map.set(envTime, peakVal);
    envTime += hold;
    map.set(envTime, peakVal);
    envTime += decay;
    map.set(envTime, sustainVal);
    if (noteDuration >= envTime) {
      map.set(noteDuration, sustainVal);
    } else {
      let prevTime = 0;
      let keys = Array.from(map.keys());
      for (let time of keys) {
        if (time > noteDuration) {
          let val = map.get(time);
          let prevVal = map.get(prevTime);
          let tratio = (noteDuration - prevTime) / (time - prevTime);
          map.set(noteDuration, prevVal * Math.pow((val + 0.01) / (prevVal + 0.01), tratio));
          break;
        }
        prevTime = time;
      }
      for (let time of keys) {
        if (time > noteDuration) {
          map.delete(time);
        }
      }
    }
    let finalEnvTime = noteDuration + release;
    map.set(finalEnvTime, finalVal);
    return map;
  }
};

// src/sound_lib/playback/_NoteBuilder.ts
var _NoteBuilder = class {
  constructor() {
    this._slm = SoundLibManager.get();
    this._init();
  }
  /**
   * removes all notes from the sequence
   */
  clearSequence() {
    this._sequence = [];
  }
  createPerformanceNotes(ndoList) {
    let pNotes = [];
    for (let ndo of ndoList) {
      pNotes.push(...this._createPerformanceNotes(ndo));
    }
    return pNotes;
  }
  /** adds a note to the performance. Access the complete list of notes in the
   *  sequence property.*/
  addNoteToSequence(ndo) {
    let notes = this._createPerformanceNotes(ndo);
    if (notes != null) {
      this._sequence.push(...notes);
    }
  }
  _createPerformanceNotes(ndo) {
    if (ndo.preset != null && ndo.bank != null) {
      this._slm.setPreset(ndo.bank, ndo.preset);
    }
    if (this._slm._libType === 0 /* SF */) {
      return PerformanceNoteDO.createPerformanceNote(
        ndo,
        this._preset
      );
    } else if (this._slm._libType === 1 /* SFZ */) {
      return PerformanceNoteDO.createNotesSfz(
        ndo,
        this._preset
      );
    }
    return null;
  }
  _init() {
    this._sequence = [];
  }
  /**
   * the notes to be performed - give these to the player/performer object
   */
  get sequence() {
    this._sequence.sort((pn1, pn2) => pn1.startTime <= pn2.startTime ? -1 : 1);
    return this._sequence;
  }
  /** the Preset object that contains the zones and Instrument you want to use
   *  to build the sequence*/
  get preset() {
    return this._preset;
  }
  set preset(value) {
    this._preset = value;
  }
};

// src/sound_lib/SoundLibManager.ts
import { createEmitter } from "ts_utils";

// src/sound_lib/playback/NoteInfoDO.ts
var NoteInfoDO = class _NoteInfoDO {
  constructor() {
  }
  static create(midiNote, startTime, duration, amplitude, pan, bank, preset) {
    let ndo = new _NoteInfoDO();
    ndo.midiNote = midiNote;
    ndo.startTime = startTime;
    ndo.duration = duration;
    ndo.amplitude = amplitude;
    ndo.pan = pan;
    ndo.bank = bank;
    ndo.preset = preset;
    return ndo;
  }
  static fromLiveNote(ldo) {
    let ndo = new _NoteInfoDO();
    ndo.midiNote = ldo.midiNote;
    ndo.amplitude = ldo.amplitude;
    ndo.pan = 0.5;
    ndo.bank = ldo.bank;
    ndo.preset = ldo.preset;
    return ndo;
  }
};

// src/sound_lib/playback/_StringNoteParser.ts
var _StringNoteParser = class {
  constructor() {
    this._volume = 0.5;
    this._pan = 0.5;
    this._hold = 1;
    this._time = 0;
    this._slm = SoundLibManager.get();
  }
  /**interprets a string to create [_PerformanceNoteDO] objects. string must be
   * structured as a sequence of space-delimited commands, each beginning with
   * a prefix and then an equals sign. Example commands are as follows:
   * 
   * tempo=96  (in bpm)
   * preset=0:23  (bank:preset)
   * volume=0.8  (0 - 1)
   * pan=0.5  (0 - 1)
   * hold=0.95  (0 - 1 or > 1 for overlap ... sets the style of connecting or
   * separating notes. 1.0 means notes will be held for exact duration value.)
   * 
   * n=60:5 or n=60:5:0.2  (pitch:duration:+/-Vol ... 7=whole, 5=quarter...)
   * r=4  (a duration from 1-8 where 5 is a quarter note)
   * c=60:5 (pitch:duration:+/-Vol) For chords. Identical to notes, but time
   * does not advance. For the last note of a chord, use n= so time will
   * advance.
   * 
   * For notes and rests, you can append a time ratio to the duration to
   * create a tuplet effect. Add an underscore after the duration type and
   * then specify the ratio as a fraction. Example, 3 eighth note triplets:
   * n=60:4_2/3   n=62:4_2/3   n=64:4_2/3
   * 
   * qntime=20  (overrides the current time based on the tempo by setting the
   * time in terms of elapsed quarter notes. You could use this to ensure
   * tuplet rounding doesn't cause issues or to jump forward/back in time)
   * */
  createNotes(notes) {
    this._reset();
    notes = notes.trim();
    let commands = notes.split(/\s+/);
    for (let cmdString of commands) {
      let cmd = cmdString.split("=");
      if (cmd.length !== 2)
        throw "invalid command string: $cmdString";
      switch (cmd[0]) {
        case "tempo":
          this._tempo = this._readNum(cmd[1]);
          break;
        case "volume":
          this._volume = this._readNum(cmd[1]);
          break;
        case "pan":
          this._pan = this._readNum(cmd[1]);
          break;
        case "hold":
          this._hold = this._readNum(cmd[1]);
          break;
        case "preset":
          this._readPreset(cmd[1]);
          break;
        case "n":
          this._readNote(cmd[1], true);
          break;
        case "r":
          this._readRest(cmd[1]);
          break;
        case "c":
          this._readNote(cmd[1], false);
          break;
        case "qntime":
          this._time = this._readNum(cmd[1]) * 60 / this._tempo;
          break;
        default:
          console.log("Invalid command: ${cmd[0]}");
      }
    }
  }
  _reset() {
    this._tempo = 90;
    this._volume = 0.5;
    this._pan = 0.5;
    this._hold = 1;
    this._time = 0;
    this._slm.clearSequence();
  }
  _readNum(cmd) {
    try {
      return Number.parseFloat(cmd);
    } catch (err) {
      throw "invalid number value";
    }
  }
  _readPreset(cmd) {
    let parts = cmd.split(":");
    if (parts.length !== 2)
      throw "invalid preset command: $cmd";
    this._slm.setPreset(
      Number.parseInt(parts[1]),
      Number.parseInt(parts[0])
    );
  }
  _readNote(cmd, advanceTime) {
    let parts = cmd.split(":");
    if (parts.length !== 2 && parts.length !== 3) {
      throw "invalid note command: $cmd";
    }
    try {
      let pitch = Number.parseInt(parts[0]);
      let duration = this._convertNoteTypeToTime(parts[1]);
      let volume = this._volume;
      if (parts.length === 3) {
        volume += Number.parseFloat(parts[2]);
        if (volume > 1)
          volume = 1;
        else if (volume < 0)
          volume = 0;
      }
      this._slm.addNoteToSequence(NoteInfoDO.create(
        pitch,
        this._time,
        duration * this._hold,
        volume,
        this._pan
      ));
      if (advanceTime)
        this._time += duration;
    } catch (err) {
      throw "invalid note command: $cmd";
    }
  }
  _readRest(cmd) {
    try {
      let duration = this._convertNoteTypeToTime(cmd);
      this._time += duration;
    } catch (err) {
      throw "invalid rest command: $cmd";
    }
  }
  _convertNoteTypeToTime(durationType) {
    try {
      let prefix = Number.parseInt(durationType[0]);
      let ratio = 1;
      if (durationType.length >= 5 && durationType.indexOf("_") === 1 && durationType.indexOf("/") !== -1) {
        let ratioStr = durationType.substring(2);
        let parts = ratioStr.split("/");
        let numerator = Number.parseFloat(parts[0]);
        let denominator = Number.parseFloat(parts[1]);
        ratio = numerator / denominator;
      }
      switch (prefix) {
        case 8:
          return 60 / this._tempo * 8 * ratio;
        case 7:
          return 60 / this._tempo * 4 * ratio;
        case 6:
          return 60 / this._tempo * 2 * ratio;
        case 5:
          return 60 / this._tempo * ratio;
        case 4:
          return 60 / this._tempo * 0.5 * ratio;
        case 3:
          return 60 / this._tempo * 0.25 * ratio;
        case 2:
          return 60 / this._tempo * 0.125 * ratio;
        case 1:
          return 60 / this._tempo * 0.0625 * ratio;
        default:
          throw "invalid durationType: $durationType";
      }
    } catch (err) {
      throw "invalid duration type: $durationType";
    }
  }
};

// src/sound_lib/playback/_SoundLibPlayer.ts
import { AudioComponent } from "audio_output";
import { BrowserUtils as BrowserUtils2 } from "ts_utils";
var _SoundLibPlayer = class extends AudioComponent {
  // StreamController<number> _notesCompleteController = 
  // 	new StreamController<number>.broadcast();
  constructor() {
    super();
    this._noteSets = [];
    //the AudioContext.currentTime value at start of play
    this._playStartTime = 0;
    this._timerInterval = 0.5;
    //in seconds
    this._window = 1;
    //in seconds
    this._phTimeSubs = [];
    this._isPlaying = false;
    this._trackGains = /* @__PURE__ */ new Map();
    this._trackPanners = /* @__PURE__ */ new Map();
    /**the value the _masterGain is set to at beginning of playback*/
    this._masterGainValue = 1;
    this._noteNodes = [];
    //stores the time of the note that ends the latest in the song so that
    //playback can be terminated when it's finished.
    this._lastNoteEndTime = -1;
    this._onTimer = () => {
      let phTime = this.currentPlayTime;
      for (let sub of this._phTimeSubs) {
        if (phTime >= sub.phTriggerTime) {
          sub.phTriggerTime = sub.callback(phTime);
          if (sub.phTriggerTime == null) {
            this.removePhTimeSub(sub.callback);
          }
        }
      }
    };
    this._getNotesInWindow = (phTime) => {
      let currentTime = this._context.currentTime;
      if (phTime < 0) {
        return Number.MIN_SAFE_INTEGER;
      }
      this._removeFinishedNodes(currentTime);
      let activePNotes = [];
      let windowCutoff = phTime + this._window;
      for (let i = this._noteSets.length - 1; i >= 0; i--) {
        let set = this._noteSets[i];
        while (set.noteIndex < set.notes.length && set.startTime + set.notes[set.noteIndex].startTime <= windowCutoff) {
          let origNote = set.notes[set.noteIndex];
          let noteStartTime = origNote.startTime + set.startTime;
          let setEndTime = noteStartTime + origNote.durationRequested;
          if (setEndTime > set.endTime) {
            set.endTime = setEndTime;
          }
          set.noteIndex++;
          if (origNote.startTime + set.startTime < phTime || set.iterationCanceled) {
            continue;
          }
          let note = origNote.clone();
          note.startTime = noteStartTime;
          note.endTime += set.startTime;
          activePNotes.push(note);
        }
        if (set.noteIndex === set.notes.length) {
          if (set.loop) {
            set.noteIndex = 0;
            set.startTime = set.endTime;
            set.iterationCanceled = false;
            i++;
          } else if (set.enableRescheduling) {
            if (set.schedule.length > 0) {
              set.noteIndex = 0;
              set.startTime = set.schedule.shift();
              set.iterationCanceled = false;
              i++;
            }
          } else {
            this._noteSets.splice(i, 1);
            let ms = ~~((set.endTime - phTime) * 1e3);
            let ev = new SoundLibEvent(
              6 /* noteSetComplete */
            );
            ev.noteSetId = set.id;
            window.setTimeout(() => {
              if (this._isPlaying)
                this._slm._dispatch(ev);
            }, ms);
          }
        }
      }
      if (activePNotes.length === 0) {
        this._checkAllNotesFinished(phTime);
        if (this._firstSoundTime == null) {
          this._firstSoundTime = this._context.currentTime;
        }
        return Number.MIN_SAFE_INTEGER;
      }
      let newNodes = this._processNotes(activePNotes, this._playStartTime);
      this._noteNodes.push(...newNodes);
      if (this._firstSoundTime == null) {
        this._firstSoundTime = this._context.currentTime;
      }
      return Number.MIN_SAFE_INTEGER;
    };
    this._slm = SoundLibManager.get();
    this._context = this._slm.context;
    if (this._context == null) {
      throw "AudioContext not set";
    }
    this._init();
  }
  /**plays notes immediately (or whenever their startTime indicates)*/
  playNotes(notes) {
    this._processNotes(notes, this._context.currentTime);
  }
  /**starts the audio playback - use prepareAudio first to generate the audio
   * The optional [startTime] can be used to adjust the start position
   * of playback. A positive value (in seconds) causes */
  play(startTime = 0) {
    if (this._isPlaying) {
      return;
    }
    for (let set of this._noteSets) {
      set.noteIndex = 0;
      set.resetStartTime();
    }
    this._playStartTime = this._context.currentTime - startTime;
    this._firstSoundTime = null;
    this._lastNoteEndTime = -1;
    this._isPlaying = true;
    this._masterGain.gain.cancelScheduledValues(0);
    this._masterGain.gain.setValueAtTime(this._masterGainValue, 0);
    this.addPhTimeSub(this._getNotesInWindow, Number.MIN_SAFE_INTEGER);
    this._timerId = window.setInterval(
      this._onTimer,
      this._timerInterval * 1e3
    );
    this._onTimer();
    this._playSilentNote();
  }
  /**stops playback. There is a 50ms delay after stop is called for volume
   * to be ramped to 0, and a Promise is returned after 0 is reached. But
   * it should be okay to call play again without waiting for this to
   * complete.*/
  stop() {
    return new Promise((resolve, reject) => {
      if (this._isPlaying) {
        if (this._timerId != null) {
          window.clearInterval(this._timerId);
          this._timerId = null;
        }
        if (this._playbackCompleteIntervalId == null) {
          window.clearInterval(this._playbackCompleteIntervalId);
          this._playbackCompleteIntervalId = null;
        }
        this._phTimeSubs = [];
        this._isPlaying = false;
        this._masterGain.gain.setValueAtTime(
          this._masterGainValue,
          this._context.currentTime
        );
        this._masterGain.gain.linearRampToValueAtTime(
          0,
          this._context.currentTime + 0.05
        );
        this._clearAllNoteSetSchedules();
        let noteNodesCopy = this._noteNodes.slice();
        this._noteNodes = [];
        window.setTimeout(() => {
          for (let noteNode of noteNodesCopy) {
            noteNode.disconnectNodes();
          }
          noteNodesCopy = null;
          resolve();
        }, 50);
      } else {
        resolve();
      }
    });
  }
  /**prepares notes for playback. If [loop] is true, the sequence will
   * be repeated continuously. Setting the [performanceTime] to a value 
   * greater than 0 will cause the sequence to be delayed until the time from
   * the start of playback has passed (not AudioContext.currentTime). If
   * adding notes during playback, use the [currentPlayTime] getter to check
   * the elapsed time (playhead time). If enableRescheduling is true, the note
   * set will not be disposed after its playback completes, and you can use
   * the scheduleNoteSet() and clearNoteSetSchedule() methods to update it for
   * repeated use.*/
  prepareAudio(notes, loop = false, performanceTime = 0, enableRescheduling = false) {
    let set = new _NoteSet(
      notes,
      loop,
      performanceTime,
      enableRescheduling
    );
    this._noteSets.push(set);
    return set.id;
  }
  clearNotes() {
    this._noteSets = [];
  }
  removeNoteSet(noteSetId) {
    let idx = this._noteSets.findIndex((n) => n.id === noteSetId);
    if (idx !== -1) {
      this._noteSets.splice(idx, 1);
      if (this._noteSets.length === 0) {
        this.stop();
      }
    }
  }
  /**stops playback of the requested NoteSet if it is currently playing.
   * This does not remove the NoteSet or prevent its next scheduled or
   * repeated entrance from playing. Use clearNoteSetSchedule() in
   * combination for that.*/
  stopNoteSetPlayback(noteSetId) {
    let set = this._noteSets.find((ns) => ns.id === noteSetId);
    if (set != null) {
      set.iterationCanceled = true;
    }
  }
  /**Schedules an additional time for the requested NoteSet to play. The
   * performanceTime is the number of seconds after the start of player
   * playback. Use [currentPlayTime] to get the current position.
  */
  scheduleNoteSet(noteSetId, performanceTime) {
    let set = this._noteSets.find((ns) => ns.id === noteSetId);
    if (set != null) {
      if (!set.enableRescheduling) {
        throw "This NoteSet was not enabled for rescheduling in the call to prepareAudio()";
      }
      set.schedule.push(performanceTime);
    }
  }
  /**clears the play schedule for the requested NoteSet */
  clearNoteSetSchedule(noteSetId, performanceTime = null) {
    let noteSet = this._noteSets.find((ns) => ns.id === noteSetId);
    if (noteSet != null) {
      if (performanceTime != null) {
        let idx = noteSet.schedule.indexOf(performanceTime);
        if (idx !== -1) {
          noteSet.schedule.splice(idx, 1);
        }
        if (noteSet.startTime === performanceTime) {
          noteSet.noteIndex = noteSet.notes.length;
        }
      } else {
        noteSet.schedule = [];
      }
    }
  }
  /**Sets the gain for a track that's associated with [NoteInfoDO.trackId]
   * (and thus [_PerformanceNoteDO.trackId]) value. If a gain node has not
   * been created yet for this index, ie. because playback hasn't been called
   * yet, it is created here. A [trackId] of 0 is meant to refer to the
   * first, default track. [volume] must be from 0.0 to 1.0.*/
  setTrackVolume(trackId, volume) {
    if (volume < 0)
      throw `invalid gain volume: ${volume}`;
    let trackGain = this._getOrCreateTrackGain(trackId);
    trackGain.gain.setValueAtTime(volume, 0);
  }
  /**Sets the pan for a track that's associated with [NoteInfoDO.trackId]
   * (and thus [_PerformanceNoteDO.trackId]) value. If a pan node has not
   * been created yet for this index, ie. because playback hasn't been called
   * yet, it is created here. A [trackId] of 0 is meant to refer to the
   * first, default track. [pan] must be from -1.0 to 1.0.*/
  setTrackPan(trackId, pan) {
    if (this._isSafari)
      return;
    if (pan < -1 || pan > 1)
      throw `invalid gain volume: ${pan}`;
    let trackPanner = this._getOrCreateTrackPanner(trackId);
    trackPanner.pan.setValueAtTime(pan, 0);
  }
  /**sets the gain for the master output. Default is 1.0. Must always be >= 0
   */
  setMasterVolume(volume) {
    if (volume < 0)
      throw `invalid gain volume: ${volume}`;
    this._masterGainValue = volume;
    this._masterGain.gain.setValueAtTime(volume, 0);
  }
  /**Adjusts the [_SoundLibPlayer]'s playback forward or back the requested
   * amount of time.*/
  shiftPlayerTime(timeDelta) {
    if (this._isPlaying) {
      let ctxTime = this._context.currentTime;
      for (let node of this._noteNodes) {
        node.gainNode.gain.cancelScheduledValues(ctxTime);
        node.gainNode.gain.linearRampToValueAtTime(0, ctxTime + 0.01);
      }
      this._playStartTime -= timeDelta;
    }
  }
  /**Adds a callback you want called when the player passes a PlayheadTime.
   * The player checks for passed trigger times on a regular interval of
   * about 500ms, so this call could be up to around that much after the
   * phTriggerTime you pass. To compensate, set the phTriggerTime to an
   * earlier value if you need to receive the event by that time. All subs
   * are removed when playback stops.
   * @param callback The function to call. It should take the current phTime
   * as an argument. It can optionally return a new phTriggerTime so you can
   * reuse this. If null is returned, the sub will be removed. If you set
   * the value to Number.MIN_SAFE_INTEGER, or any value less than or equal
   * to the current playhead time, it will ensure the callback is called on
   * the next iteration.
   * @param phTriggerTime The phTime in the player that you want the callback
   * to be called _after_.*/
  addPhTimeSub(callback, phTriggerTime) {
    if (this._phTimeSubs.some((pts) => pts.callback === callback)) {
      return;
    }
    let sub = new _PhTimeSub();
    sub.callback = callback;
    sub.phTriggerTime = phTriggerTime;
    this._phTimeSubs.push(sub);
  }
  /**Removes a Playhead time subscription via its callback.
   * @returns true if the callback was found and removed. */
  removePhTimeSub(callback) {
    let idx = this._phTimeSubs.findIndex((pts) => pts.callback === callback);
    if (idx !== -1) {
      this._phTimeSubs.splice(idx, 1);
      return true;
    } else {
      return false;
    }
  }
  _init() {
    this._masterGain = this._context.createGain();
    this._isSafari = BrowserUtils2.isSafari();
  }
  _playSilentNote() {
    let bs = this._context.createBufferSource();
    bs.buffer = this._context.createBuffer(
      1,
      1024,
      this._context.sampleRate
    );
    bs.start(this._context.currentTime);
  }
  _removeFinishedNodes(currentTime) {
    for (let i = this._noteNodes.length - 1; i >= 0; i--) {
      let nodeGroup = this._noteNodes[i];
      if (nodeGroup.endContextTime < currentTime) {
        nodeGroup.disconnectNodes();
        this._noteNodes.splice(i, 1);
      }
    }
  }
  _checkAllNotesFinished(elapsedTime) {
    let finished = true;
    for (let set of this._noteSets) {
      if (set.noteIndex < set.notes.length || set.loop || set.enableRescheduling) {
        finished = false;
        break;
      }
    }
    if (finished) {
      window.clearInterval(this._timerId);
      let msUntilPlaybackComplete = ~~((this._lastNoteEndTime - elapsedTime) * 1e3);
      this._playbackCompleteIntervalId = window.setTimeout(async () => {
        if (this._isPlaying) {
          await this.stop();
          this._slm._dispatch(new SoundLibEvent(
            5 /* playbackFinished */
          ));
        }
      }, msUntilPlaybackComplete);
      return true;
    }
    return false;
  }
  /**plays a list of [_PerformanceNoteDO] objects. Notes will be offset
   * in time by their startTime from a baseline playStartTime value
   * that is usually set when AudioContext.currentTime is recorded
   * at the beginning of playback. If you just want to play notes
   * immediately, initialize playStartTime to AudioContext.currentTime.
   * @param pNoteDOs the list of notes to play
   * @param playStartTime a value associated with the beginning of
   * playback - all notes will have this value added to their start
   * time.*/
  _processNotes(pNoteDOs, playStartTime) {
    let noteNodes = [];
    for (let note of pNoteDOs) {
      if (note.startTime + note.durationRequested > this._lastNoteEndTime) {
        this._lastNoteEndTime = note.startTime + note.durationRequested;
      }
      if (note.amplitude === 0) {
        continue;
      }
      let noteContextTime = note.startTime + playStartTime;
      let duration = note.endTime - note.startTime;
      let bs = this._context.createBufferSource();
      bs.buffer = note.audioBuffer;
      let gainNode = _NoteNodes.gainNodesPool.length > _NoteNodes.minPoolSize ? _NoteNodes.gainNodesPool.shift() : this._context.createGain();
      let lastTimeKey = 0;
      for (let timeKey of note.volumeMap.keys()) {
        let vol = note.volumeMap.get(timeKey);
        if (vol === 0)
          vol = 1e-4;
        try {
          if (timeKey - lastTimeKey < 0.1) {
            gainNode.gain.linearRampToValueAtTime(
              vol,
              noteContextTime + timeKey
            );
          } else {
            gainNode.gain.exponentialRampToValueAtTime(
              vol,
              noteContextTime + timeKey
            );
          }
        } catch (err) {
          console.log(err);
        }
        lastTimeKey = timeKey;
      }
      bs.connect(gainNode);
      let trackNode = this._isSafari ? this._getOrCreateTrackGain(note.trackId) : this._getOrCreateTrackPanner(note.trackId);
      let filterNode;
      if (note.lowpassFreq != null) {
        if (_NoteNodes.filterNodesPool.length > _NoteNodes.minPoolSize) {
          filterNode = _NoteNodes.filterNodesPool.shift();
        } else {
          filterNode = this._context.createBiquadFilter();
        }
        filterNode.type = "lowpass";
        for (let timeKey of note.filterMap.keys()) {
          let frq = note.filterMap.get(timeKey);
          if (frq === 0)
            frq = 1e-4;
          else if (frq > 2e4)
            frq = 2e4;
          filterNode.frequency.setValueAtTime(frq, noteContextTime + timeKey);
        }
        gainNode.connect(filterNode);
        filterNode.connect(trackNode);
      } else {
        gainNode.connect(trackNode);
      }
      bs.playbackRate.setValueAtTime(note.pitchStretchRatio, 0);
      if (note.loop) {
        bs.loopStart = note.loopStartPos / bs.buffer.sampleRate;
        bs.loopEnd = note.loopEndPos / bs.buffer.sampleRate;
        bs.loop = bs.loopEnd < duration;
      }
      bs.start(
        noteContextTime
        /* , 0, duration */
      );
      bs.stop(noteContextTime + duration);
      noteNodes.push(new _NoteNodes(
        noteContextTime + duration,
        bs,
        gainNode,
        filterNode
      ));
    }
    return noteNodes;
  }
  _getOrCreateTrackGain(trackId) {
    let trackGain = this._trackGains.get(trackId);
    if (trackGain == null) {
      trackGain = this._context.createGain();
      this._trackGains.set(trackId, trackGain);
      trackGain.connect(this._masterGain);
    }
    return trackGain;
  }
  _getOrCreateTrackPanner(trackId) {
    let trackPan = this._trackPanners.get(trackId);
    if (trackPan == null) {
      trackPan = this._context.createStereoPanner();
      this._trackPanners.set(trackId, trackPan);
      let trackGain = this._getOrCreateTrackGain(trackId);
      trackPan.connect(trackGain);
    }
    return trackPan;
  }
  _clearAllNoteSetSchedules() {
    for (let noteSet of this._noteSets) {
      noteSet.schedule = [];
    }
  }
  get isPlaying() {
    return this._isPlaying;
  }
  /**returns the AudioContext time when play is called.*/
  get playStartTime() {
    return this._playStartTime;
  }
  /**the AudioContext.currentTime value after the first audio processing call
   * completes*/
  get firstSoundTime() {
    return this._firstSoundTime;
  }
  /**returns the amount of time in seconds that has elapsed since the start
   * of playback.*/
  get currentPlayTime() {
    return this._isPlaying ? this._context.currentTime - this._playStartTime : 0;
  }
  get outputNode() {
    return this._masterGain;
  }
};
var __NoteNodes = class __NoteNodes {
  constructor(endContextTime, bufferSourceNode, gainNode, filterNode) {
    this.endContextTime = endContextTime;
    this.bufferSourceNode = bufferSourceNode;
    this.gainNode = gainNode;
    this.filterNode = filterNode;
  }
  disconnectNodes() {
    this.bufferSourceNode.disconnect(0);
    this.gainNode.disconnect(0);
    this.gainNode.gain.cancelScheduledValues(0);
    __NoteNodes.gainNodesPool.push(this.gainNode);
    if (this.filterNode != null) {
      this.filterNode.disconnect(0);
      this.filterNode.frequency.cancelScheduledValues(0);
      __NoteNodes.filterNodesPool.push(this.filterNode);
    }
  }
};
__NoteNodes.gainNodesPool = [];
__NoteNodes.filterNodesPool = [];
//it seems as if there may be a delay when using cancelScheduledValues to
//ready a gain node for reuse. When nodes are reused immediately after
//disconnected, they are sometimes silent. The minPoolSize is meant to
//generally afford a little time between when a node is "readied" and when
//it is actually used again.
__NoteNodes.minPoolSize = 10;
var _NoteNodes = __NoteNodes;
var __NoteSet = class __NoteSet {
  constructor(notes, loop, startTime, enableScheduling) {
    /**the context time that the last note finishes (before looping if 
     * applicable). This is updated dynamically as notes are played.*/
    this.endTime = 0;
    /**index of the next note that will be added to the playback que*/
    this.noteIndex = 0;
    /**array of start time second values in terms of player.currentPlayTime.
     * When set playback completes, if times have been added to this schedule,
     * the note set will start again when the time at schedule[0] is reached.
     * That time will then be removed from the array.*/
    this.schedule = [];
    /**if true, the current iteration of the NoteSet should not be played. This
     * gets reset to false upon loop or the next scheduled entrance. */
    this.iterationCanceled = false;
    this.notes = notes;
    this.loop = loop;
    this.startTime = startTime;
    this.enableRescheduling = enableScheduling;
    if (enableScheduling) {
      this.schedule = [];
    }
    this._originalStartTime = startTime;
    this.id = __NoteSet.idCounter;
    __NoteSet.idCounter++;
  }
  /**if this [_NoteSet] has looped during playback, its [startTime] may no 
   * longer be what it originally was. This resets it for a new play session.
   * */
  resetStartTime() {
    this.startTime = this._originalStartTime;
    this.endTime = 0;
  }
};
__NoteSet.idCounter = 0;
var _NoteSet = __NoteSet;
var _PhTimeSub = class {
};

// src/sound_lib/playback/_LiveNotePlayer.ts
import { AudioComponent as AudioComponent2 } from "audio_output";
import { BrowserUtils as BrowserUtils3 } from "ts_utils";
var _LiveNotePlayer = class extends AudioComponent2 {
  constructor(noteBuilder) {
    super();
    this._isEnabled = false;
    /**the value the _masterGain is initialized to*/
    this._masterGainValue = 0.3;
    this._notesMap = /* @__PURE__ */ new Map();
    this._onCleanupTime = () => {
      let ldoList = Array.from(this._notesMap.keys());
      let cTime = this._context.currentTime;
      for (let i = ldoList.length - 1; i >= 0; i--) {
        let ldo = ldoList[i];
        let noteNodes = this._notesMap.get(ldo);
        if (noteNodes.endContextTime <= cTime) {
          noteNodes.disconnectNodes();
          this._notesMap.delete(ldo);
        }
      }
    };
    this._slm = SoundLibManager.get();
    this._noteBuilder = noteBuilder;
    this._context = this._slm.context;
    if (this._context == null)
      throw "AudioContext not set";
    this._init();
  }
  get isEnabled() {
    return this._isEnabled;
  }
  enable() {
    if (this._isEnabled) {
      return;
    }
    this._cleanupIntervalId = window.setInterval(this._onCleanupTime, 5e3);
    this._isEnabled = true;
  }
  disable() {
    if (!this._isEnabled) {
      return;
    }
    this.stopAllLiveNotes();
    window.clearInterval(this._cleanupIntervalId);
    this._cleanupIntervalId = null;
    this._isEnabled = false;
  }
  startLiveNote(ldo) {
    let ndo = NoteInfoDO.fromLiveNote(ldo);
    ndo.startTime = this._context.currentTime;
    ndo.duration = 100;
    let pNotes = this._noteBuilder.createPerformanceNotes([ndo]);
    this._notesMap.set(ldo, this._playNote(pNotes[0]));
  }
  stopLiveNote(ldo, fadeDuration = 0.05) {
    let noteNodes = this._notesMap.get(ldo);
    if (noteNodes != null) {
      let cTime = this._context.currentTime;
      noteNodes.stopBufferSource(cTime + fadeDuration + 0.01);
      noteNodes.endContextTime = cTime + fadeDuration + 0.01;
      noteNodes.gainNode.gain.cancelScheduledValues(0);
      noteNodes.gainNode.gain.linearRampToValueAtTime(0, cTime + fadeDuration);
    }
  }
  stopAllLiveNotes() {
    let ldoList = Array.from(this._notesMap.keys());
    for (let i = ldoList.length - 1; i >= 0; i--) {
      let ldo = ldoList[i];
      let noteNodes = this._notesMap.get(ldo);
      noteNodes.stopBufferSource(this._context.currentTime);
      noteNodes.disconnectNodes();
      this._notesMap.delete(ldo);
    }
  }
  _init() {
    this._masterGain = this._context.createGain();
    this._masterGain.gain.cancelScheduledValues(0);
    this._masterGain.gain.setValueAtTime(this._masterGainValue, 0);
    this._isSafari = BrowserUtils3.isSafari();
  }
  /**plays a list of [_PerformanceNoteDO] objects. Notes will be offset
   * in time by their startTime from a baseline playStartTime value
   * that is usually set when AudioContext.currentTime is recorded
   * at the beginning of playback. If you just want to play notes
   * immediately, initialize playStartTime to AudioContext.currentTime.
   * @param pNoteDOs the list of notes to play
   * @param playStartTime a value associated with the beginning of
   * playback - all notes will have this value added to their start
   * time.*/
  _playNote(note) {
    let noteContextTime = note.startTime;
    let duration = note.endTime - note.startTime;
    let bs = this._context.createBufferSource();
    bs.buffer = note.audioBuffer;
    let gainNode = _LiveNoteNodes.gainNodesPool.length > 0 ? _LiveNoteNodes.gainNodesPool.pop() : this._context.createGain();
    let lastTimeKey = 0;
    for (let timeKey of note.volumeMap.keys()) {
      let vol = note.volumeMap.get(timeKey);
      if (vol === 0)
        vol = 1e-4;
      try {
        if (timeKey - lastTimeKey < 0.1) {
          gainNode.gain.linearRampToValueAtTime(
            vol,
            noteContextTime + timeKey
          );
        } else {
          gainNode.gain.exponentialRampToValueAtTime(
            vol,
            noteContextTime + timeKey
          );
        }
      } catch (err) {
        console.log(err);
      }
      lastTimeKey = timeKey;
    }
    bs.connect(gainNode);
    let filterNode;
    if (note.lowpassFreq != null) {
      if (_LiveNoteNodes.filterNodesPool.length > 0) {
        filterNode = _LiveNoteNodes.filterNodesPool.pop();
      } else {
        filterNode = this._context.createBiquadFilter();
      }
      filterNode.type = "lowpass";
      for (let timeKey of note.filterMap.keys()) {
        let frq = note.filterMap.get(timeKey);
        if (frq === 0)
          frq = 1e-4;
        else if (frq > 2e4)
          frq = 2e4;
        filterNode.frequency.setValueAtTime(frq, noteContextTime + timeKey);
      }
      gainNode.connect(filterNode);
      filterNode.connect(this._masterGain);
    } else {
      gainNode.connect(this._masterGain);
    }
    bs.playbackRate.setValueAtTime(note.pitchStretchRatio, 0);
    if (note.loop) {
      bs.loopStart = note.loopStartPos / bs.buffer.sampleRate;
      bs.loopEnd = note.loopEndPos / bs.buffer.sampleRate;
      bs.loop = bs.loopEnd < duration;
    }
    bs.start(noteContextTime);
    return new _LiveNoteNodes(
      noteContextTime + duration,
      bs,
      gainNode,
      filterNode
    );
  }
  get outputNode() {
    return this._masterGain;
  }
};
var __LiveNoteNodes = class __LiveNoteNodes {
  constructor(endContextTime, bufferSourceNode, gainNode, filterNode) {
    this._bufferSourceNodeStopped = false;
    this.endContextTime = endContextTime;
    this.bufferSourceNode = bufferSourceNode;
    this.gainNode = gainNode;
    this.filterNode = filterNode;
  }
  disconnectNodes() {
    this.bufferSourceNode.disconnect(0);
    this.gainNode.disconnect(0);
    this.gainNode.gain.cancelScheduledValues(0);
    __LiveNoteNodes.gainNodesPool.push(this.gainNode);
    if (this.filterNode != null) {
      this.filterNode.disconnect(0);
      this.filterNode.frequency.cancelScheduledValues(0);
      __LiveNoteNodes.filterNodesPool.push(this.filterNode);
    }
  }
  stopBufferSource(stopTime) {
    if (!this._bufferSourceNodeStopped) {
      this.bufferSourceNode.stop(stopTime);
      this._bufferSourceNodeStopped = true;
    }
  }
};
__LiveNoteNodes.gainNodesPool = [];
__LiveNoteNodes.filterNodesPool = [];
var _LiveNoteNodes = __LiveNoteNodes;

// src/sound_lib/SoundLibManager.ts
var SoundLibManager = class _SoundLibManager {
  constructor(context, soundLibType, outputTarget) {
    this._eventEmitter = createEmitter();
    this._phTimeEmitter = createEmitter();
    if (context == null) {
      throw "AudioContext must be set.";
    }
    if (soundLibType == null) {
      throw "SoundLibType must be set";
    }
    _SoundLibManager._ref = this;
    this._context = context;
    this._libType = soundLibType;
    this._init();
    if (outputTarget != null) {
      this.setOutputTarget(outputTarget);
    }
  }
  /**Gets a stream of different types of events the [SoundLibManager] 
   * dispatches. Check the [SoundLibEvent.type] property.*/
  get soundLibEvent() {
    return this._eventEmitter.event;
  }
  get phTimeEvent() {
    return this._phTimeEmitter.event;
  }
  /**Retrieves the SoundLibManager instance, creating if necessary. Parameters
   * only need to be passed on first creation.
   * @param context AudioContext used for loading and playing the sound
   * presets.
   * @param soundLibType supports either SF or SFZ.
   * @param outputTarget refers to the destination for the audio from the 
   * _SoundLibPlayer. This must be set to hear audio.*/
  static get(context, soundLibType, outputTarget) {
    if (_SoundLibManager._ref == null) {
      new _SoundLibManager(context, soundLibType, outputTarget);
    }
    return _SoundLibManager._ref;
  }
  /**Sets the destination of the audio from the [_SoundLibPlayer]. The
   * [outputTarget] must override the [AudioComponent.inputNode] getter.*/
  setOutputTarget(outputTarget) {
    this._player.disconnectAll();
    this._player.connect(outputTarget);
    this._outputTarget = outputTarget;
  }
  /////////////////////LIBRARY LOADING//////////////////////////
  /**
  * Loads a sound map that identifies the path to preset files. The [path]
  * should point to the json map file. For details on the format, see the
  * [_SoundMap.fromJson] constructor.*/
  async loadSoundMap(path) {
    await this._loader.loadSoundMap(path);
  }
  /**Loads and parses a SoundFont (Sf2, Sf3) or SFZ file. In the case of an
   * SFZ file, the library should be a zip archive containing folders named
   * according to bank number, each containing preset files that begin with
   * the preset number. Example SFZ folder structure:
   * ```
   * [pianos_ogg.zip]
   * 	[000]
   * 		[000_Stereo Grand.sfz]
   * 		[001_Bright Grand.sfz]
   * 	[samples]
   * 		[Grand Piano-A#1.ogg]
   * 		...
   * ```*/
  async loadLibrary(path, loadSfzPresets = true) {
    await this._loader.loadLibrary(path, loadSfzPresets);
  }
  /**Loads or completes the loading of presets. If [loadLibrary] was already
   * used to load an SFZ zip archive and presets have been initialized but
   * not completely parsed, this completes the process for those presets. If
   * there are additional requested presets, this method next looks for a
   * map file loaded with [loadSoundMap]. If one exists, it is checked for
   * presets matching the requested presets. The [bankPresetPairs] is ordered
   * by bank/preset pairs (bank,preset,bank,preset, etc.). If 
   * [bankPresetPairs] is null, this loads (or finishes loading) all presets.
   * 
   * By default, sounds will be downloaded from the server unless you have
   * specified a [dbName] for an indexedDb.*/
  async loadPresets(bankPresetPairs) {
    if (bankPresetPairs == null) {
      await this._loader.loadAllPresets();
    } else {
      await this._loader.loadPresets(bankPresetPairs);
    }
  }
  /**Checks to see if a preset matching the requested [bankNumber] and
   * [presetNumber] exists.*/
  hasPreset(bankNumber, presetNumber) {
    return this._loader.hasPreset(bankNumber, presetNumber);
  }
  /**/
  //////////////////NOTE SEQUENCING/////////////////////////*/
  setPreset(bankNumber, presetNumber) {
    let preset = this._loader.getPreset(bankNumber, presetNumber);
    if (preset == null) {
      throw `Preset ${presetNumber} in bank ${bankNumber} doesn't exist.`;
    }
    this._sequenceBuilder.preset = preset;
  }
  /**adds a single note to the sequence. When finished adding notes to your
   * sequence, call [prepareSequencedPlayback] to add them to the player
   * as a note set.*/
  addNoteToSequence(noteDO) {
    this._sequenceBuilder.addNoteToSequence(noteDO);
  }
  /**removes notes from the sequence builder so that calling 
   * [addNoteToSequence] will begin creating a new set of notes. This does not
   * remove notes from the player. Use [clearNotes] to accomplish that.*/
  clearSequence() {
    this._sequenceBuilder.clearSequence();
  }
  /**returns a list of PerformanceNoteDO objects that have been created via
   * calls to addNoteToSequence(). In most cases you can skip this step and
   * use prepareSequencedPlayback() to send the sequenced notes to the
   * player.*/
  getPerformanceNotes() {
    return this._sequenceBuilder.sequence;
  }
  createNotesFromString(notes) {
    this._stringNoteMaker.createNotes(notes);
  }
  ///////////////////PLAYBACK////////////////////////
  /**Converts NoteInfoDOs to PerformanceNoteDOs and sends them to the player
   * for immediate playback (or whenever their startTimes indicate)*/
  playNotes(notes) {
    let pNotes = this._sequenceBuilder.createPerformanceNotes(notes);
    this._player.playNotes(pNotes);
  }
  enableLiveNotePlayer() {
    if (this._outputTarget == null) {
      throw "An output AudioComponent must be set before enabling the LiveNotePlayer";
    }
    if (this._liveNotePlayer == null) {
      this._liveNotePlayer = new _LiveNotePlayer(this._sequenceBuilder);
      this._liveNotePlayer.connect(this._outputTarget);
    }
    this._liveNotePlayer.enable();
  }
  disableLiveNotePlayer() {
    this._liveNotePlayer.disable();
  }
  /**starts playing a note. Use [stopNote] to terminate.*/
  startLiveNote(ldo) {
    if (this._liveNotePlayer == null || !this._liveNotePlayer.isEnabled) {
      throw "you must call SoundLibManager.enableLiveNotePlayer() before";
      "using it";
    }
    this._liveNotePlayer.startLiveNote(ldo);
  }
  /**stops playing a note started with [startNote].*/
  stopLiveNote(ldo, fadeDuration = 0.05) {
    if (this._liveNotePlayer == null || !this._liveNotePlayer.isEnabled) {
      throw "you must call SoundLibManager.enableLiveNotePlayer() before using it";
    }
    this._liveNotePlayer.stopLiveNote(ldo, fadeDuration);
  }
  /**stops all live notes immediately, with no fade out*/
  stopAllLiveNotes() {
    this._liveNotePlayer.stopAllLiveNotes();
  }
  /**starts the audio playback - use prepareAudio first to generate the audio
   * The optional [startTime] can be used to adjust the start position
   * of playback. A positive value (in seconds) causes */
  play(startTime = 0) {
    this._player.play(startTime);
  }
  /**stops playback. There is a 50ms delay after stop is called for volume
   * to be ramped to 0, and a Promise is returned after 0 is reached. But
   * it should be okay to call play again without waiting for this to
   * complete.*/
  async stop() {
    this._player.stop();
  }
  /**prepares notes that were added via [addNoteToSequence] for playback. 
   * If [loop] is true, the sequence will be repeated continuously. 
   * Setting the [performanceTime] to a value 
   * greater than 0 will cause the sequence to be delayed until the time from
   * the start of playback has passed (not AudioContext.currentTime). If
   * adding notes during playback, use the [playStartTime] getter to get the
   * time (in seconds) that playback started. You can compare this to
   * [AudioContext.currentTime] to figure out how much time has elapsed since
   * playback start. If enableRescheduling is true, the note set will not be
   * disposed after its playback completes, and you can use the
   * scheduleNoteSet() and clearNoteSetSchedule() methods to update it for
   * repeated use. Returns an integer id associated with this note set
   * sequence which may be used later to remove this note set via
   * [removeNoteSet].*/
  prepareSequencedPlayback(loop = false, performanceTime = 0, enableRescheduling = false) {
    let pNotes = this._sequenceBuilder.sequence;
    return this._player.prepareAudio(
      pNotes,
      loop,
      performanceTime,
      enableRescheduling
    );
  }
  /**removes all note sequences from the player.*/
  clearNotes() {
    this._player.clearNotes();
  }
  /**removes a specific noteset from the player*/
  removeNoteSet(notesetId) {
    this._player.removeNoteSet(notesetId);
  }
  /**stops playback of the requested NoteSet if it is currently playing.
   * This does not remove the NoteSet or prevent its next scheduled or
   * repeated entrance from playing. Use clearNoteSetSchedule() in
   * combination for that.*/
  stopNoteSetPlayback(noteSetId) {
    this._player.stopNoteSetPlayback(noteSetId);
  }
  /**Adds PerformanceNoteDOs to the player, generating a note set that will
   * be added to the process loop.
   * If [loop] is true, the sequence will be repeated continuously. 
   * Setting the [performanceTime] to a value 
   * greater than 0 will cause the sequence to be delayed until the time from
   * the start of playback has passed (not AudioContext.currentTime). If
   * adding notes during playback, use the [playStartTime] getter to get the
   * time (in seconds) that playback started. You can compare this to
   * [AudioContext.currentTime] to figure out how much time has elapsed since
   * playback start. If enableRescheduling is true, the note set will not be
   * disposed after its playback completes, and you can use the
   * scheduleNoteSet() and clearNoteSetSchedule() methods to update it for
   * repeated use. Returns an integer id associated with this note set
   * sequence which may be used later to remove this note set via
   * [removeNoteSet].*/
  addPerformanceNotes(notes, loop = false, performanceTime = 0, enableRescheduling = false) {
    return this._player.prepareAudio(
      notes,
      loop,
      performanceTime,
      enableRescheduling
    );
  }
  /**Schedules an additional time for the requested NoteSet to play. The
   * performanceTime is the number of seconds after the start of player
   * playback. Use [currentPlayTime] to get the current position.
  */
  scheduleNoteSet(noteSetId, performanceTime) {
    this._player.scheduleNoteSet(noteSetId, performanceTime);
  }
  /**clears the play schedule for the requested NoteSet. If a performanceTime
   * is included, only that time is removed from the schedule */
  clearNoteSetSchedule(noteSetId, performanceTime = null) {
    this._player.clearNoteSetSchedule(noteSetId, performanceTime);
  }
  /**Sets the gain for a track that's associated with [NoteInfoDO.trackId]
   * (and thus [_PerformanceNoteDO.trackId]) value. If a gain node has not
   * been created yet for this index, ie. because playback hasn't been called
   * yet, it is created here. A [trackId] of 0 is meant to refer to the
   * first, default track. [volume] must be from 0.0 to 1.0.*/
  setTrackVolume(trackId, volume) {
    this._player.setTrackVolume(trackId, volume);
  }
  /**Sets the pan for a track that's associated with [NoteInfoDO.trackId]
   * (and thus [_PerformanceNoteDO.trackId]) value. If a pan node has not
   * been created yet for this index, ie. because playback hasn't been called
   * yet, it is created here. A [trackId] of 0 is meant to refer to the
   * first, default track. [pan] must be from -1.0 to 1.0.*/
  setTrackPan(trackId, pan) {
    this._player.setTrackPan(trackId, pan);
  }
  /**sets the gain for the master output. Default is 0.3. Must always be >= 0
   */
  setMasterVolume(volume) {
    this._player.setMasterVolume(volume);
  }
  /**Adjusts the [_SoundLibPlayer]'s playback forward or back the requested
   * amount of time.*/
  shiftPlayerTime(timeDelta) {
    this._player.shiftPlayerTime(timeDelta);
  }
  /**Adds a callback you want called when the player passes a PlayheadTime.
   * The player checks for passed trigger times on a regular interval of
   * about 500ms, so this call could be up to around that much after the
   * phTriggerTime you pass. To compensate, set the phTriggerTime to an
   * earlier value if you need to receive the event by that time. All subs
   * are removed when playback stops.
   * @param callback The function to call. It should take the current phTime
   * as an argument. It can optionally return a new phTriggerTime so you can
   * reuse this. If null is returned, the sub will be removed. If you set
   * the value to Number.MIN_SAFE_INTEGER, or any value less than or equal
   * to the current playhead time, it will ensure the callback is called on
   * the next iteration.
   * @param phTriggerTime The phTime in the player that you want the callback
   * to be called _after_.*/
  addPhTimeSub(callback, phTriggerTime) {
    this._player.addPhTimeSub(callback, phTriggerTime);
  }
  /**Removes a Playhead time subscription via its callback.
   * @returns true if the callback was found and removed. */
  removePhTimeSub(callback) {
    return this._player.removePhTimeSub(callback);
  }
  _init() {
    this._loader = new _SoundLibLoader();
    this._sequenceBuilder = new _NoteBuilder();
    this._stringNoteMaker = new _StringNoteParser();
    this._player = new _SoundLibPlayer();
  }
  /**for internal package use */
  _dispatch(e) {
    this._eventEmitter.emit(e);
  }
  /**gets the [AudioContext] used for loading and playback.*/
  get context() {
    return this._context;
  }
  get isPlaying() {
    return this._player.isPlaying;
  }
  /**returns the AudioContext time when the playback started.*/
  get playStartTime() {
    return this._player.playStartTime;
  }
  /**the AudioContext.currentTime value after the first audio processing call
   * completes*/
  get firstSoundTime() {
    return this._player.firstSoundTime;
  }
  /**returns the amount of time in seconds that has elapsed since the start
   * of playback.*/
  get currentPlayTime() {
    return this._player.currentPlayTime;
  }
  /**returns the [SoundLibType] that was set for this [SoundLibManager].
   * read-only*/
  get libType() {
    return this._libType;
  }
  /**returns true if a sound map has been loaded.*/
  get soundMapLoaded() {
    return this._loader._soundMap != null;
  }
  /**The name of an indexedDb database. Example: 'odogy_soundsDb'. Map json 
   * files that specify file versions will attempt to load from this db. If
   * null (default), no db will be used and files will always be downloaded.*/
  get dbName() {
    return this._loader._dbName;
  }
  set dbName(value) {
    this._loader._dbName = value;
  }
};

// src/sound_lib/playback/LiveNoteInfoDO.ts
var LiveNoteInfoDO = class {
  constructor(midiNote, amplitude, bank, preset) {
    this.midiNote = midiNote;
    this.amplitude = amplitude;
    this.bank = bank;
    this.preset = preset;
  }
};
export {
  LiveNoteInfoDO,
  NoteInfoDO,
  SoundLibEvent,
  SoundLibEventType,
  SoundLibManager,
  SoundLibType
};
